/**
 * components/workspace/CvSection.jsx — RC 1.49.7 Phase 1
 *
 * Live CV section for the route-based Application Workspace.
 *
 * Mirrors the wizard's CV-step contract verbatim — same endpoints,
 * same idempotency key minting, same in-flight recovery marker, same
 * three-control footer. The container changes (modal → routed page);
 * the financial protections do not.
 *
 * Hard rails (operator-locked, RC 1.49.x):
 *
 *   • RC 1.49.x.RUN_ISOLATION (operator 2026-05-06): NEVER pre-
 *     hydrate a historical CV variant on mount. The user must
 *     explicitly click Generate to surface a tailored CV. The
 *     ONE allowed exception is the in-flight CV marker recovery
 *     branch (the marker is written by the user's own Generate
 *     click in this session).
 *
 *   • RC 1.49.7 Phase 1 belt-and-braces job-scoping (operator
 *     2026-02): any recovered/reused CV variant MUST match
 *       (a) marker key (user_id, job_id) — implicit from
 *           sessionStorage key shape
 *       (b) variant.base_cv_id === current primaryCv.id
 *       (c) variant.content_json.idempotency_key === current
 *           cached idempotency key, when one exists.
 *     A variant failing ANY guard is discarded and the marker
 *     cleared. The workspace MUST NEVER prefill a CV variant from
 *     another job, even if it is the latest variant for the user.
 *
 *   • RC 1.49.1 P0-FIN: financial idempotency key minted via the
 *     SAME `getOrMintIdempotencyKey(user, job, 'cv_variant')`
 *     helper as the wizard. A retry within `IDEMPOTENCY_TTL_MS`
 *     reuses the SAME key so `credit_ledger`'s partial unique
 *     index returns the prior reservation row instead of debiting
 *     a second time.
 *
 *   • RC 1.49.1 P0-A: long-running LLM call wrapped in
 *     `withTimeoutRecovery` so a 30s ingress proxy 504 surfaces a
 *     calm recovery toast instead of a double-charge attempt.
 *
 *   • RC 1.49.5A P0-A3: chargeable button hard-disabled while
 *     `creditsLoading` is true. No client-side pre-flight charge
 *     can fire before the wallet hydrates.
 *
 *   • Generate ≠ Continue — three separate controls. The user may
 *     SKIP CV generation entirely and continue with the Source CV.
 *
 *   • RC 1.49.7 C5 (operator 2026-02) — three pre-flight guards
 *     ported inline from the wizard. STRICTLY non-floating:
 *       (G1) Baseline refresh guard — fires on
 *            `GET /baseline-status → stale_refresh_available` OR
 *            `POST /generate → 409 BASELINE_VERSION_STALE`.
 *       (G2) Soft reject-gate — fires on
 *            `POST /generate → 409 analyse_fit_reject`.
 *            Pack-canonical bypass pre-sets `force_generate=true`
 *            on the first call so packed jobs never see it.
 *       (G3) High-CV Value Guard — fires PRE-LLM when the baseline
 *            `cv_sub_score >= HIGH_CV_THRESHOLD (80)` AND the user
 *            has not already acknowledged it this session.
 *     All three guards render inline — NO Dialog / Sheet / Drawer /
 *     Popover / Modal / floating panel is used. Generate is hard-
 *     disabled while any guard is active.
 *
 *   • Pricing label: ALWAYS sourced from
 *     `getActionCost(usageCosts, 'cv_improve')`, never hard-coded.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Sparkles, Loader2, FileText, CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import GenerateCvOptionsSheet from '@/components/wizard/GenerateCvOptionsSheet';
// ── U-14.C (2026-02-15) — restore the Improvement / Defensibility
// explanation card group in the workspace CV section. This
// component was rendered by the legacy wizard modal but was lost
// during the RC 1.49.7 Phase 1 Workspace-Route migration. The
// underlying data (`cv_variants.optimisation_artifacts`) is still
// generated and persisted by the backend; the workspace just
// stopped rendering it. Restoring the surface here closes the
// gap. Component itself returns `null` when artefacts are absent,
// so non-charged / pre-P1 variants render unchanged.
import OptimisationDefenseCards from '@/components/wizard/OptimisationDefenseCards';
// ── Bundle U-9.G — Loaded-from-history chip (2026-02-15) ────────
import HistoryBadge from '@/components/workspace/HistoryBadge';
import { apiFetch } from '@/lib/api';
// RC 1.59 Slice 4 — close the CV Variant Generate-complete →
// cv-variants list invalidation gap. The Documents library and any
// other surface that reads `KEYS.cvVariantsAll()` (e.g. ApplicationsPage's
// variant subload) sees the new variant on the next mount/visit
// without waiting for the 5-minute TTL.
import { invalidate as cacheInvalidate } from '@/lib/listCache';
import {
  useCredits, getActionCost,
} from '@/lib/creditsContext';
import { handleCreditsError } from '@/lib/handleCreditsError';
import {
  getOrMintIdempotencyKey,
  clearIdempotencyKey,
  peekIdempotencyKey,
} from '@/lib/idempotencyKeys';
// RC 1.57 Generation Session Guard — see lib/generationSession.js.
// Keeps a generation session alive past finalize-charge so a
// remount / tab-switch landing in the hydration gap reuses the
// SAME idempotency key (no new variant minted).
import {
  getOrMintSession,
  markTerminalSuccess,
  markHydrationStable,
  releaseSessionIfStable,
  resetSession,
} from '@/lib/generationSession';
import {
  writeInflightCvMarker,
  readInflightCvMarker,
  clearInflightCvMarker,
  isInflightMarkerExpired,
  findVariantByTitleStamp,
} from '@/lib/inflightCvRecovery';
import { GEN_CV_DEFAULTS } from '@/lib/cvDefaults';
import { stripMarkdownTokens } from '@/lib/markdownStrip';
// RC 1.53 Phase 4.2 — OperationLifecycleController integration. The
// section continues to own idempotency-key derivation, the inflight
// marker, finalize-charge, URL writes, credits.refresh(), and the
// cooldown timer. The controller is layered ON TOP of the existing
// flow: it wraps the polling-helper call in a per-(jobId, actionCode)
// dispatch so accidental concurrent generation is a hard no-op (Risk
// #1 mitigation) and a `pack:artefact_ready` event fires for Phase
// 4.3 consumers (PackSection) once finalize-charge confirms. The
// direct `generateCvVariantWithPolling` import has been removed
// (Phase 4.2 — the adapter lazy-imports it inside `run()`).
import * as cvVariantAdapter from '@/lib/operationController/adapters/cvVariantAdapter';
import {
  dispatch as controllerDispatch,
  emit as controllerEmit,
  OPERATION_EVENT_TYPES,
  useOperationLifecycle,
  verifyAndHydrateCvVariant,
  trace as actionTrace,
  initLifecycleSubscribers,
  registerCacheInvalidator,
  getStatusMessage,
} from '@/lib/operationController';
import { useArtefactRehydrate } from '@/lib/operationController/useArtefactRehydrate';
import WorkspaceActivityIndicator from '@/components/workspace/WorkspaceActivityIndicator';
// RC 1.52 Phase 3A.4 — UX cooldown after successful Generate
// dispatch so the button can't be re-clicked on a stale balance.
import {
  startCooldown, cvVariantCooldownKey, useCooldown,
} from '@/lib/actionCooldown';

// ─── RC 1.49.7 C5 — High-CV Value Guard constants (mirror wizard) ──
// The wizard defines these at L123 / L128 of JobApplicationWizard.jsx.
// Workspace uses the IDENTICAL sessionStorage key shape so an operator
// who acknowledges the guard in one container is not re-prompted in
// the other within the same session. HIGH_CV_THRESHOLD is a pure
// client-side UX trigger — the authoritative charge gate remains
// server-side in `finalize-charge` and is score-delta-driven.
const HIGH_CV_THRESHOLD = 80;
const highCvSessionKey = (jid) => `ndor_high_cv_confirmed_${jid}`;

// ─── RC 1.52 Phase 3A.DUP — system-name minting via shared helper ──
// CV Variant titles are SYSTEM-generated and collision-proof
// ({Role} CV — YYYY-MM-DD HH:MM:SS.mmm — XXXX). The user does NOT
// rename generated CVs in the UI; they may delete or export-rename.
// Backend's duplicate-title guard remains as defence-in-depth.
import { mintCvVariantTitle } from '@/lib/titleStamps';

export default function CvSection({
  job,
  jobId,
  primaryCv,
  onExit,
  onContinue,
  onBusyChange,
  // RC 1.53 Phase 4.2.1.1.d — operator-locked workspace-wide
  // Generate-lock. When TRUE, every Generate-class action in this
  // section is disabled (Generate CV button, reject-guard "Generate
  // anyway", high-CV-guard "Generate anyway"). Navigation
  // (Continue / Exit) stays free. Sourced from the parent
  // workspace's busy aggregator + controller `isAnyRunning(jobId)`.
  workspaceBusy = false,
  // ── Bundle U-9.B props (2026-02-15) ─────────────────────────────
  // `lookupStatus` ∈ {'idle','checking','ready'} — passed down from
  // ApplicationWorkspacePage's `useWorkspaceArtefactDiscovery` hook.
  // Generate is locked while !== 'ready'.
  // `loadedFromHistory` is true when the mount-time discovery hook
  // identified an existing CV variant for this (user, job) — drives
  // the `<HistoryBadge />` rendered next to the section title.
  lookupStatus = 'ready',
  loadedFromHistory = false,
  rootTestId = 'workspace-cv-section',
}) {
  const credits = useCredits();
  const usageCosts = credits?.usageCosts;
  const creditsLoading = !credits || !!credits.loading;

  // RC 1.49.7 Phase 2 — CV → Letter carry-forward via URL params.
  // The user's currently-selected CV (Source vs. tailored Variant)
  // is mirrored to `?source_cv_id=` + `?source_cv_type=` so the
  // Cover Letter section + a browser refresh / share link can
  // reconstruct the exact carry-forward without any in-memory
  // hand-off. Read-only here on the CV side; the write effect is
  // below. URL is the source of truth across sections.
  const [searchParams, setSearchParams] = useSearchParams();

  // Live pricing — sourced from /api/account/plan via creditsContext.
  const cvCost = getActionCost(usageCosts, 'cv_improve');
  const cvCostLabel = cvCost == null
    ? null
    : `${cvCost} ${cvCost === 1 ? 'Credit' : 'Credits'}`;

  // ── Selection state ──────────────────────────────────────────────
  // RC 1.49.7 pre-Phase-5 hardening (operator 2026-02): seed
  // `selectedCv` from URL params on mount BEFORE falling back to
  // primaryCv. Without this, refreshing the CV section silently
  // downgraded a user's tailored-variant selection back to the
  // Source CV (the URL-write effect would then overwrite the URL
  // with the wrong selection).
  //
  // URL is the source of truth across sections — CV writes it,
  // Letter + Fit read it. The seed below makes CV ALSO read it on
  // mount, closing the asymmetry.
  const [selectedCv, setSelectedCv] = useState(() => {
    const urlId = searchParams.get('source_cv_id');
    const urlType = searchParams.get('source_cv_type');
    if (urlId && (urlType === 'cv_variant' || urlType === 'base_cv')) {
      return {
        type: urlType,
        id:   urlId,
        // Display name resolved by a later effect once the variant
        // row is in hand; placeholder for now.
        name: urlType === 'cv_variant' ? 'Tailored Variant' : 'Source CV',
      };
    }
    return null;
  });
  useEffect(() => {
    if (selectedCv) return;
    if (primaryCv?.id) {
      setSelectedCv({
        type: 'base_cv',
        id: primaryCv.id,
        name: primaryCv.original_filename || 'Source CV',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryCv?.id]);

  // Once primaryCv loads, refine the placeholder display name on
  // a Source-CV URL seed so the card shows the real filename.
  useEffect(() => {
    if (!primaryCv?.id) return;
    setSelectedCv((prev) => {
      if (!prev) return prev;
      if (prev.type !== 'base_cv') return prev;
      if (prev.id !== primaryCv.id) return prev;
      const realName = primaryCv.original_filename || 'Source CV';
      if (prev.name === realName) return prev;
      return { ...prev, name: realName };
    });
  }, [primaryCv?.id, primaryCv?.original_filename]);

  // ── Generated / recovered variant state ──────────────────────────
  const [generatedResult, setGeneratedResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  // RC 1.50 Workspace UX Slice 0 — emit busy state to parent so the
  // header back-to-job link, Continue, and section-nav switching
  // are all blocked while a Generate is mid-flight. Read-only —
  // `setBusy` is a stable React useState setter so no useCallback
  // dance is needed.
  useEffect(() => {
    onBusyChange?.(generating);
  }, [generating, onBusyChange]);
  // RC 1.49.7 Layer-A* UX (operator 2026-02-09 PM) — inline
  // "Restoring your tailored CV…" indicator while the URL-driven
  // mount-rehydrate (Slice 2, lines 506-578) is in flight. Set
  // before the read, cleared in finally regardless of cancellation
  // (mirroring PackSection's `setHydrating(false)` pattern). The
  // banner only renders when `restoring && !generatedResult` so a
  // successful rehydrate that beats the paint cycle is silent.
  const [restoring, setRestoring] = useState(false);

  // RC 1.50 R.2 — inline draft preview parity with CoverLetterSection
  // (line 723 onwards). The Letter section truncates its draft to
  // 6 lines / ~1200 chars in a calm monospace block under the
  // success card. CV gets the same treatment. Inline only — no
  // modal / dialog / drawer / popover. Expand/collapse is a pure
  // state-flip; the preview is rendered as a SIBLING after the
  // existing `<button data-testid="workspace-cv-generated-card">`
  // (which stays a button so the slice 2 source-grep test pin
  // `data-testid="workspace-cv-generated-card"[\s\S]*?</button>`
  // remains green — no nested-button HTML invalidity introduced).
  // Read-only over `generatedResult.generated_text`; never charged.
  const [cvPreviewExpanded, setCvPreviewExpanded] = useState(false);

  // ── RC 1.49.7 Slice 2 — In-flight recovery status (inline UX) ────
  // 'idle'       → no marker, or marker bound to a row already
  // 'recovering' → marker present, variant row not yet found; we
  //                are polling /cv-variants for a deterministic
  //                title-stamped match. NEVER triggers /generate.
  // 'exhausted'  → marker present, retries exhausted, no row found;
  //                advisory inline message points to Documents. The
  //                marker is cleared so a future remount won't loop.
  // Mirrors the wizard's silent-retry shape (timeoutRecovery.js) but
  // surfaces calm inline copy instead of a toast — workspace-scope.
  const [recoveryStatus, setRecoveryStatus] = useState('idle');
  // RC 1.53 Phase 4.2.1 — auxiliary state that captures the latest
  // backend `run_status` (or any progress_label surfaced by the
  // polling envelope) so the status copy area can render a calm,
  // backend-driven phrase ("Tailoring your CV…", "Analysing the job
  // requirements…") instead of a generic spinner. Cleared when the
  // operation reaches a terminal state.
  const [progressLabel, setProgressLabel] = useState(null);

  // RC 1.53 Phase 4.2.1 — subscribe to the controller's per-(job,
  // action) lifecycle. The hook is purely additive: `controllerStatus`
  // tracks idle → dispatching → polling → stabilizing →
  // complete/failed/aborted. The existing `generating` boolean still
  // owns the CTA gate; the hook drives the visible status copy +
  // activity indicator only. Phase 4.2.1.1 — `markComplete` /
  // `markFailed` are the section-driven terminal transitions; they
  // keep the activity indicator mounted through the entire
  // stabilization chain (finalize-charge, credits refresh,
  // hydration, cache invalidation) until ALL work is done.
  const {
    status: controllerStatus,
    markComplete: lifecycleMarkComplete,
    markFailed: lifecycleMarkFailed,
  } = useOperationLifecycle(jobId, 'cv_variant');

  // RC 1.53 Phase 4.2.1 — bootstrap the global lifecycle subscribers
  // (cache invalidators, trace logger). Idempotent — safe to call
  // from every section mount.
  useEffect(() => {
    initLifecycleSubscribers();
  }, []);

  // RC 1.53 Phase 4.2.1 — register the CV variants list-cache
  // invalidator. Documents library auto-refreshes on terminal-
  // complete without a hard refresh. The callback re-derives by
  // re-fetching `/api/cv-variants` next time the library mounts —
  // the existing `apiFetch` layer's listCache key is invalidated
  // via window event.
  useEffect(() => {
    const unsub = registerCacheInvalidator('cv_variant', (detail) => {
      actionTrace('cv_variant', 'cache_invalidated', { jobId: detail?.jobId });
      // Defensive global signal that the Documents library + any
      // other consumer can listen for. Phase 4.3 will replace this
      // with a richer listCache.invalidate() once a registry exists.
      try {
        window.dispatchEvent(new CustomEvent('ndor:cv-variants:invalidated', {
          detail,
        }));
      } catch { /* noop SSR */ }
    });
    return unsub;
  }, []);

  // ── RC 1.53 Phase 4.2.1.1 — Orphan Variant Mount Sweep ──────────
  //
  // Belt-and-braces guarantee for "successful persisted output →
  // exactly one charge". On mount, probe the two known sources of
  // an uncharged-but-complete variant:
  //
  //   (a) the inflight marker (sessionStorage) — set by a Generate
  //       click in this tab; if the user closed the tab during
  //       finalize-charge, the marker survives and we recover.
  //   (b) `?last_variant_id=` URL param — set after a successful
  //       Generate; survives full refresh.
  //
  // For each candidate variantId, GET `/api/cv-variants/{id}` and,
  // if `run_status='complete'` AND `charged_at` is null, POST
  // `/api/cv-variants/{id}/finalize-charge`. The endpoint is
  // replay-safe (server `charged_at` guard returns idempotent on
  // already-charged rows) and refuses to charge non-complete rows
  // (409 queued/running, 422 failed) — so this sweep is safe to
  // run on every mount with no double-charge risk.
  //
  // Read-only finding (operator-locked, 2026-05-12): a cross-tab
  // sweep keyed on `(user_id, job_id)` would require the list
  // endpoint to expose `charged_at` + `run_status`; this is left
  // as a separate read-only proposal and NOT implemented here.
  useEffect(() => {
    if (!jobId || !primaryCv?.user_id) return undefined;
    let cancelled = false;
    (async () => {
      const candidates = new Set();
      try {
        const marker = readInflightCvMarker(primaryCv.user_id, jobId);
        if (marker?.variantId) candidates.add(marker.variantId);
      } catch { /* noop */ }
      const urlLastVariantId = searchParams.get('last_variant_id');
      if (urlLastVariantId) candidates.add(urlLastVariantId);
      if (!candidates.size) return;
      for (const variantId of candidates) {
        if (cancelled) return;
        let row;
        try {
          // eslint-disable-next-line no-await-in-loop
          row = await apiFetch(`/api/cv-variants/${variantId}`);
        } catch { row = null; }
        if (cancelled) return;
        if (!row || typeof row !== 'object') continue;
        const status = (row.run_status || 'complete').toLowerCase();
        const isComplete = status === 'complete';
        const isUncharged = !row.charged_at;
        if (!isComplete || !isUncharged) continue;
        actionTrace('cv_variant', 'orphan_sweep_finalize_charge', {
          variantId, run_status: status, charge_reason: row.charge_reason || null,
        });
        try {
          // eslint-disable-next-line no-await-in-loop
          await apiFetch(
            `/api/cv-variants/${variantId}/finalize-charge`,
            { method: 'POST', body: { job_id: jobId } },
          );
          if (typeof credits?.refresh === 'function') {
            // eslint-disable-next-line no-await-in-loop
            try { await credits.refresh(); } catch { /* noop */ }
          }
        } catch (err) {
          // Fail-open. 409 queued/running and 422 failed are
          // expected for non-complete rows; we already gated above.
          // Any other transient is acceptable — the next mount will
          // retry.
          // eslint-disable-next-line no-console
          console.warn(
            '[Workspace CvSection] orphan sweep finalize-charge skipped:',
            variantId, err?.status || err?.message,
          );
        }
      }
    })();
    return () => { cancelled = true; };
    // Bound to jobId + primaryCv.user_id only — fires once per
    // (job, user) mount. Intentionally NOT bound to `searchParams`
    // so URL hops don't re-run the sweep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, primaryCv?.user_id]);

  // ── U-2.CV — Canonical by-job mount fallback (2026-02-15) ────────
  //
  // OPERATOR-LOCKED. Closes the marker-lost edge case: when (a) the
  // SS marker is lost (hard refresh / private mode / mobile background
  // tab kill), AND (b) the URL carries no `?source_cv_id` /
  // `?last_variant_id` handle → CV section was previously blind to
  // any terminal variant that already existed for this job. Fix:
  // mount-only canonical lookup against the new
  // `GET /api/cv-variants/by-job/{jobId}?latest=true` endpoint;
  // if a variant is found, stamp `?last_variant_id` so the existing
  // orphan-sweep effect (L370) binds it via the canonical hydrator
  // path. Read-only. NEVER dispatches. NEVER charges.
  //
  // Strict-rail: only runs when the section has NO existing CV
  // selection AND no marker/URL handle. Never overrides an explicit
  // user choice.
  useEffect(() => {
    if (!jobId || !primaryCv?.user_id) return undefined;
    // U-11.CV.A (2026-02-15) — `source_cv_id` and `last_variant_id`
    // are ORTHOGONAL state. Pre-U-11 this effect bailed when
    // `source_cv_id` was set, even though `last_variant_id` was
    // not — net effect: the discovery hook also bailed on the same
    // race-loser guard → CV stayed unbound. Now we only skip when
    // `last_variant_id` is ALREADY stamped (idempotent).
    if (searchParams.get('last_variant_id')) return undefined;
    try {
      const _marker = readInflightCvMarker(primaryCv.user_id, jobId);
      if (_marker?.variantId) return undefined;
    } catch { /* noop */ }

    let cancelled = false;
    (async () => {
      try {
        const env = await apiFetch(
          `/api/cv-variants/by-job/${encodeURIComponent(jobId)}?latest=true`,
        );
        if (cancelled) return;
        if (!env || env.found !== true) return;
        const variantId = env.cv_variant_id;
        if (!variantId) return;
        // Stamp the URL handle so the existing orphan-sweep +
        // selection effects bind the variant naturally. We use
        // `last_variant_id` (the canonical CvSection terminal handle)
        // so the next sweep cycle reads it.
        try {
          const _next = new URLSearchParams(searchParams);
          _next.set('last_variant_id', variantId);
          if (typeof setSearchParams === 'function') {
            setSearchParams(_next, { replace: true });
          }
        } catch { /* noop */ }
        // ── U-5.CV.B — session-aware mount rebind (2026-02-15) ────
        //
        // OPERATOR-LOCKED. Closes Race #1 in the cross-tool race
        // audit: when this fallback rebinds an existing variant
        // from history, any session-guard token still alive in
        // sessionStorage (from the original generation, still
        // within SESSION_TTL_MS=10 min OR inside the 30 s
        // STABILISATION_WINDOW_MS) would otherwise be reused on
        // the next Generate click. The backend would dedupe via
        // `credit_ledger.idempotency_key` and return `reused=true`
        // — surfacing as "We reused your existing tailored CV"
        // even though the user explicitly clicked Generate.
        //
        // Fix: when the U-2.CV fallback binds history, advance the
        // session to `hydrationStable` AND request a conditional
        // release. The next deliberate Generate click then mints
        // a FRESH idempotency key → real generation, real charge.
        // The 30-second-old session is no longer in effect.
        //
        // Read-only — never dispatches, never charges. Defensive
        // no-op when the session helper is absent or fails.
        try {
          markHydrationStable(primaryCv.user_id, jobId, 'cv_variant');
          releaseSessionIfStable(primaryCv.user_id, jobId, 'cv_variant');
        } catch { /* noop — best-effort */ }
      } catch (err) {
        // 404 / 401 / table missing / network — silently degrade.
        // The marker.variantId path + URL handle remain authoritative.
        // eslint-disable-next-line no-console
        console.warn(
          '[Workspace CvSection] by-job lookup skipped:',
          err?.status || err?.message,
        );
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, primaryCv?.user_id]);

  // ── RC 1.49.7 C5 — Pre-flight guard state (workspace inline) ─────
  // Three mutually-exclusive guards, one state per guard. Each guard
  // is either `null` (inactive) or a plain object carrying the data
  // needed by its inline panel + its confirm handler. None of them
  // use floating UI — they render inline inside the section.
  //
  //   baselineGuard = null                               → not open
  //   baselineGuard = {                                   → open
  //     baseline_version, current_version,
  //     highCvConfirmed,          // carry-forward from G3 path
  //     busy,                     // null | 'refresh' | 'decline'
  //   }
  //
  //   rejectGuard = null                                  → not open
  //   rejectGuard = {                                     → open
  //     fit_score, cv_sub_score, threshold, final_decision,
  //     highCvConfirmed, busy,
  //   }
  //
  //   highCvGuard = null                                  → not open
  //   highCvGuard = {                                     → open
  //     source_cv_score, busy,
  //   }
  const [baselineGuard, setBaselineGuard] = useState(null);
  const [rejectGuard, setRejectGuard] = useState(null);
  const [highCvGuard, setHighCvGuard] = useState(null);

  // ─── RC 1.51 follow-up A — CV Generation Options Sheet + Continue
  //     warning (operator-locked, 2026-02). Two pieces of local
  //     state, both UI-only:
  //       • optionsOpen    — controls the GenerateCvOptionsSheet
  //                          slide-over visibility. Set on the
  //                          user's Generate-button click; cleared
  //                          by the sheet's own onOpenChange or by
  //                          its Confirm handler. The resolved
  //                          {template_type, tone_type,
  //                          length_preference,
  //                          additional_cv_context} trio is
  //                          captured into each guard's `cvOptions`
  //                          field so the user's chosen options are
  //                          NEVER lost when a guard intervenes
  //                          between sheet-confirm and the
  //                          chargeable POST.
  //       • continueWarnOpen — alert-dialog visibility when the
  //                          user clicks Continue while a tailored
  //                          variant exists AND the active source
  //                          is the Source/Base CV (Task 1).
  const [optionsOpen, setOptionsOpen] = useState(false);
  // ── U-5.CV.A — Regenerate confirm gate state (2026-02-15) ────────
  //
  // OPERATOR-LOCKED. `null` (gate closed) | `{ source: 'cv_generate' }`
  // (gate open — awaiting user confirm). Set by `handleGenerate`
  // when a variant is ALREADY bound, intercepting the dispatch flow
  // before the options sheet opens. Cleared on user confirm or
  // cancel. Mirrors `regenerateConfirm` in InterviewPrepSection.jsx.
  const [regenerateConfirm, setRegenerateConfirm] = useState(null);

  // RC 1.53 Phase 4.3 — Double-click race fix (operator, 2026-05-12 PM).
  // `submittingCv` covers the window between the user clicking
  // "Generate" inside the options sheet and the operation controller
  // taking over (`generating=true` after async pre-flights). Without
  // this flag the main Generate button is clickable for the ~1s
  // pre-flight window post-sheet-close, leading to confirmed 2×
  // chargeable double-runs in soak. Locked the moment the sheet
  // confirms; cleared if a guard intercepts (so the guarded re-
  // entry button can take over).
  const [submittingCv, setSubmittingCv] = useState(false);
  const [continueWarnOpen, setContinueWarnOpen] = useState(false);

  // ── RC 1.49.7 C5 — read-only /baseline-fit hydration ─────────────
  // Needed by the High-CV Value Guard pre-flight (reads
  // `baseline.sub_scores.cv_sub_score`). Zero-charge GET. Fail-open —
  // if the fetch fails the guard silently does not fire; the backend
  // charge gate remains authoritative.
  //
  // RC 1.49.7 C6/G3 (operator 2026-02) — the original C5 implementation
  // gated this effect with a `baselineFitAttemptedRef.current` flip-
  // before-fetch + cancelled cleanup. In React StrictMode (dev/preview
  // `yarn start`) the double-invoke produced a race: run 1 fired the
  // fetch and returned cleanup → cleanup #1 set `cancelled = true` →
  // run 2 saw the ref already flipped and early-returned → run 1's
  // fetch resolved with `cancelled === true` and SKIPPED `setBaselineFit`.
  // Net effect: the High-CV Value Guard could never fire because
  // `baselineFit` stayed null forever. Mirroring the wizard's
  // unconditional bootstrap fetch (JobApplicationWizard.jsx L1689)
  // is StrictMode-safe by construction — drop the ref guard, keep
  // the cancelled flag, accept one extra dev-only fetch.
  const [baselineFit, setBaselineFit] = useState(null);
  useEffect(() => {
    if (!jobId || !primaryCv?.user_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/jobs/${jobId}/baseline-fit`);
        if (!cancelled) setBaselineFit(res || null);
      } catch {
        // Fail open — server gate remains authoritative.
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, primaryCv?.user_id]);

  // ── URL-write side-effect: mirror selectedCv into URL params ─────
  // Whenever the user switches selection — or the recovery / generate
  // branches bind a variant — write `?source_cv_id` + `?source_cv_type`
  // so Phase 2's Cover Letter section reads the same source. `replace`
  // is true so we don't pollute browser history with selection hops.
  //
  // RC 1.49.7 H1 (Phase 4 audit, operator 2026-02): when the active
  // CV source ACTUALLY changes (URL had a prior source AND it differs
  // from the new one), invalidate downstream URL state generated
  // against the previous source. Phase 4 carry-forwards:
  //   • `?cover_letter_id` — a letter tailored for variant-A is
  //     stale once the user switches to Source CV / variant-B.
  // `?section`, `?source_cv_*`, and any unrelated params are
  // preserved by `new URLSearchParams(searchParams)` cloning. Only
  // explicit `next.delete(...)` calls remove anything.
  //
  // Rules honoured:
  //   • Initial mount (no prior `source_cv_id` in URL) → no clear.
  //   • Re-render with the same source → no-op (early return).
  //   • Source actually changed → clear `cover_letter_id` ALONGSIDE
  //     the source-write so a single `setSearchParams` call updates
  //     atomically with `replace: true` (no history spam, no
  //     intermediate stale-URL frame).
  useEffect(() => {
    if (!selectedCv?.id || !selectedCv?.type) return;
    const next = new URLSearchParams(searchParams);
    const prevId = next.get('source_cv_id');
    const prevType = next.get('source_cv_type');
    if (
      prevId === selectedCv.id
      && prevType === selectedCv.type
    ) return; // no-op if already in sync (avoids a router re-render loop)
    const sourceActuallyChanged =
      !!prevId && (prevId !== selectedCv.id || prevType !== selectedCv.type);
    next.set('source_cv_id', selectedCv.id);
    next.set('source_cv_type', selectedCv.type);
    if (sourceActuallyChanged) {
      // RC 1.49.7 H1 (operator-locked, 2026-02): invalidate the
      // letter binding because it was tailored for the prior source.
      // Backend `_validate_pack_refs` does not couple letter →
      // base_cv_id / cv_variant_id, so the workspace must enforce
      // semantic alignment client-side. PackSection then sees
      // `coverLetterId === null` and the user must generate a new
      // letter on the next visit to the Letter section.
      next.delete('cover_letter_id');
      // U-13.CV.D (2026-02-15) — also invalidate the interview-prep
      // URL handle on source change. Pre-U-13 only `cover_letter_id`
      // and the analyse_fit triple were cleared, but the IP URL-
      // bound rebind (post-U-10) had no context guard — so a stale
      // `?interview_prep_id` would silently rebind under the NEW
      // source. U-13.IP.B re-instates the FE guard, and this clear
      // closes the URL-state side of the same defect.
      next.delete('interview_prep_id');
      // RC 1.49.7 Slice 5 (operator-locked, 2026-02): same logic
      // for the analyse-fit URL triple — a fit row computed for
      // variant-A is invalid the moment the user switches to
      // Source CV / variant-B. PackSection's `urlTripleMatchesSource`
      // gate would already reject the mismatch, but clearing the
      // params here keeps the URL clean across nav and prevents
      // any future reader from re-introducing a stale binding.
      next.delete('analyse_fit_id');
      next.delete('analyse_fit_source_type');
      next.delete('analyse_fit_source_id');
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCv?.id, selectedCv?.type]);

  // ── RC 1.51 follow-up #2 (operator-locked, 2026-02) ───────────
  //    State separation: a generated Tailored CV is a VISIBLE
  //    artefact, independent of the currently-selected ACTIVE
  //    source. Switching `selectedCv` MUST NOT delete
  //    `generatedResult` — the previously-generated variant card
  //    keeps rendering as a "previously generated" surface so the
  //    user can re-select it later. The "Regenerate" label is
  //    selection-aware (see L1048-…) so it only reads as
  //    "Regenerate" when the active selection IS the bound
  //    variant; otherwise it reads "Generate".
  //
  //    Refresh / navigation survival is handled below by the
  //    `last_variant_id` URL-param rehydrate effect (which fires
  //    for ANY source type, not only `source_cv_type=cv_variant`).

  // ── RC 1.51 follow-up #2 (operator-locked, 2026-02) ───────────
  //    Refresh / navigation survival for the visible-artefact
  //    card. Reads `last_variant_id` from the URL on mount /
  //    URL change and rebinds `generatedResult` for the card
  //    render path. Read-only — no charge — fail-open on network
  //    error.
  //
  //    Hard rails:
  //      • Runs IRRESPECTIVE of `source_cv_type` so the variant
  //        card stays visible after the user switches back to
  //        the Source/Base CV.
  //      • No-op when `generatedResult` is already bound to the
  //        same variant (avoids redundant fetches).
  //      • Bound to `(jobId, urlLastVariantId)` only, not to
  //        `selectedCv` — the card is independent of the active
  //        selection.
  //
  // RC 1.55 U3 (operator-locked, 2026-05-13) — extracted into a
  // stable `useCallback` so it can be driven by BOTH the existing
  // mount-time URL effect AND the new `useArtefactRehydrate` hook
  // (visibility / popstate). READ-ONLY canonical GET. No charge.
  // No dispatch. No finalize-charge. No idempotency mutation. No
  // alignment-guard drift.
  const urlLastVariantId = searchParams.get('last_variant_id');
  const refetchVariantFromUrl = useCallback(async () => {
    if (!urlLastVariantId || !jobId) return;
    if (generatedResult?.variant_id === urlLastVariantId) return;
    // While Generate is mid-flight, the dispatch's own binding
    // path owns the next state write — don't race it.
    if (generating) return;
    try {
      const v = await apiFetch(`/api/cv-variants/${urlLastVariantId}`);
      if (!v || !v.id) return;
      // RC 1.56 P4 (operator-locked, 2026-05-13 PM) —
      // Presentation-only metadata preservation on URL-bound
      // rehydrate. Pre-RC1.56 this setGeneratedResult call
      // mapped only `variant_id` / `variant_name` /
      // `generated_text` — every score / governance / billing
      // descriptor was dropped, so the success card fell into
      // the legacy "Billing details — —" fallback (line 2254-
      // 2267) and looked visually different from a live-return
      // result.
      //
      // The GET endpoint at `routes/cv_variants.py:1490` does
      // `select=*`, so `content_json` (carrying `fit_summary`
      // and `cv_sub_score_variant`), `role_family`, and
      // `created_at` are already on the wire. Mapping them
      // here is pure presentation — no charge, no dispatch,
      // no idempotency mutation, no new request.
      //
      // `reused: true` triggers the existing
      // "We loaded your existing tailored CV for this job —
      // no new Credits charged" caption at line 2158-2159.
      //
      // The fields `delta` / `reason` / `usage_consumed` /
      // `charge_percentage` are NOT persisted on the row today
      // (computed once at finalize-charge). Intentionally
      // left absent so the legacy fallback continues to render
      // for any pre-RC1.56 variant — exact same behaviour as
      // today for unhydrated billing telemetry.
      const cj = (v.content_json && typeof v.content_json === 'object')
        ? v.content_json
        : {};
      // ── U-13.CV (2026-02-15) — billing metadata parity on URL
      // rebind. PRE-U-13 the URL-rebind path mapped only the
      // presentation fields, omitting `charged / reason / delta /
      // usage_consumed`. With all three of `reason/delta/usage_
      // consumed` null, the legacy "Billing details — —" fallback
      // at L2521-L2534 fired even though the row WAS charged.
      //
      // Billing telemetry IS persisted on the row as top-level
      // columns (`charged_at`, `charge_reason`, `cv_sub_score_at_
      // finalize`) plus `optimisation_artifacts.analytics_metadata.
      // variant_uplift_delta` for the delta. The finalize-charge
      // replay branch in `routes/cv_variants.py:2231-L2263` reads
      // exactly these fields — we mirror the same mapping here.
      // `usage_consumed` is not row-persisted (only present on the
      // first-call response), so we intentionally do NOT set it —
      // but with `reason` set, the legacy fallback's
      // `reason == null && delta == null && usage_consumed == null`
      // condition is broken and the charge-line at L2470 renders.
      const optArtefacts = (v.optimisation_artifacts && typeof v.optimisation_artifacts === 'object')
        ? v.optimisation_artifacts : {};
      const analyticsMeta = (optArtefacts.analytics_metadata && typeof optArtefacts.analytics_metadata === 'object')
        ? optArtefacts.analytics_metadata : {};
      const cvSubVariantPersisted = v.cv_sub_score_at_finalize
        ?? cj.cv_sub_score_variant
        ?? null;
      const cvSubSourcePersisted = analyticsMeta.source_cv_sub_score ?? null;
      let deltaPersisted = analyticsMeta.variant_uplift_delta;
      if (deltaPersisted == null
          && cvSubVariantPersisted != null
          && cvSubSourcePersisted != null) {
        const _d = Number(cvSubVariantPersisted) - Number(cvSubSourcePersisted);
        deltaPersisted = Number.isFinite(_d) ? _d : null;
      }
      setGeneratedResult({
        variant_id:           v.id,
        variant_name:         v.variant_name || v.name || null,
        generated_text:       v.content_markdown || v.generated_text || null,
        role_family:          v.role_family || null,
        fit_summary:          cj,
        cv_sub_score_variant: cvSubVariantPersisted,
        cv_sub_score_source:  cvSubSourcePersisted,
        created_at:           v.created_at || null,
        reused:               true,
        // U-13.CV — billing telemetry from row columns. The success
        // card's reason-line (L2470) renders the right phrasing per
        // `reason` value; delta drives the "Δ +X pts vs Source CV"
        // chip. `usage_consumed` is intentionally omitted (not row-
        // persisted; first-call only).
        charged:              !!v.charged_at,
        reason:               v.charge_reason || null,
        delta:                deltaPersisted,
        tolerance_band:       5,
        // U-14.C (2026-02-15) — surface the persisted
        // `optimisation_artifacts` so the workspace's
        // <OptimisationDefenseCards /> renders the
        // "Why am I stronger now?" / "How do I defend this at
        // interview?" panels on URL-rebind, not just on live
        // generate. Without this the explanation panels appeared
        // immediately after generation but disappeared on refresh
        // — exactly the symptom the operator reported.
        optimisation_artifacts: v.optimisation_artifacts || null,
      });
      // RC 1.55 Phase 1 (operator-locked, 2026-05-13) — terminal
      // side-effects on successful new canonical rebind. Mirrors
      // the dispatch path's post-terminal block so the wallet pill
      // / Documents library / Pack badge stay coherent after a
      // tab-return-only completion. READ-ONLY surface — no new
      // POST, no generation re-dispatch, no charge-settlement
      // re-call, no idempotency mutation. Fail-open per-call.
      //
      // NOTE: the orphan-variant mount-sweep (L355-L411) continues
      // to be the ONLY surface that probes the settlement endpoint
      // for an uncharged-but-complete variant. We do NOT add a
      // settlement POST here — the residual visibility-only-tab-
      // switch edge case is deferred to Phase 2 per operator
      // direction.
      try { if (typeof credits?.refresh === 'function') credits.refresh(); }
      catch { /* noop */ }
      try {
        window.dispatchEvent(new CustomEvent('ndor:cv-variants:invalidated', {
          detail: { jobId, variantId: v.id, reason: 'rehydrate_rebind' },
        }));
      } catch { /* noop — SSR / older browsers */ }
      try {
        controllerEmit(OPERATION_EVENT_TYPES.PACK_ARTEFACT_READY, {
          jobId,
          actionCode: 'cv_variant',
          artefactId: v.id,
          reused: true,
        });
      } catch { /* noop */ }
    } catch {
      // Fail-open — the user can still click Generate to
      // produce a fresh variant. The URL key is left intact in
      // case the variant becomes readable later.
    }
  }, [urlLastVariantId, jobId, generatedResult?.variant_id, generating]);

  // Mount-time URL effect — unchanged contract, now delegates to
  // the shared callback. Runs once per dependency change.
  useEffect(() => {
    if (!urlLastVariantId || !jobId) return undefined;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await refetchVariantFromUrl();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchVariantFromUrl, urlLastVariantId]);

  // RC 1.55 U3 — visibility / popstate-driven re-fetch. The hook
  // installs `visibilitychange` + `popstate` listeners and calls
  // `refetchVariantFromUrl()` on tab-return or browser back/forward.
  // Gated off while a generate is in-flight so the dispatch's own
  // binding path always wins. READ-ONLY GET. No POST. No charge.
  useArtefactRehydrate({
    enabled: !!urlLastVariantId
      && !!jobId
      && !generating,
    fetchAndBind: refetchVariantFromUrl,
  });

  // ── In-flight recovery (the ONLY allowed mount-time hydration) ──
  //
  // Scope: marker key is `inflight-cv:${userId}:${jobId}`, so any
  // marker we read is already tied to the current (user, job) pair.
  // The belt-and-braces guards below additionally verify base_cv_id
  // and idempotency_key BEFORE binding the variant — so a stale row
  // belonging to a different (job, source CV, attempt) can never be
  // surfaced even if the title happened to match.
  //
  // RC 1.49.7 C6/G3 (operator 2026-02) — `recoveryAttemptedRef`
  // dropped to be StrictMode-safe (same race as the baseline-fit
  // effect above). The `cancelled` flag + dep-array equality keep
  // this idempotent without needing a flip-before-fetch ref.
  useEffect(() => {
    if (!jobId || !primaryCv?.user_id || !primaryCv?.id) return undefined;

    let cancelled = false;
    (async () => {
      const marker = readInflightCvMarker(primaryCv.user_id, jobId);
      if (!marker) return;            // No prior in-flight Generate.

      if (isInflightMarkerExpired(marker)) {
        clearInflightCvMarker(primaryCv.user_id, jobId);
        if (!cancelled) setRecoveryStatus('exhausted');
        return;
      }

      // RC 1.51 follow-up B1 (operator-locked, 2026-02) —
      // ID-DRIVEN RECOVERY (preferred path).
      //
      // When the marker carries a `variantId`, drive recovery
      // deterministically off the canonical row via `GET /api/cv-
      // variants/{id}` instead of scanning by title. The row's
      // `run_status` field tells us exactly what to do:
      //   • 'complete' → hydrate `setGeneratedResult(...)` using
      //     the SAME call site as a sync 200 (no sparse envelope),
      //     bind `selectedCv` to the variant (URL-write effect at
      //     L364 then stamps the URL with the variant id),
      //     surface a calm restore toast, and fire the idempotent
      //     `finalize-charge` probe (server `charged_at` guard
      //     dedupes; no double charge).
      //   • 'queued' | 'running' → keep the recovering banner up
      //     and poll until terminal state.
      //   • 'failed' → surface calm retry copy and clear the
      //     marker so subsequent mounts don't loop.
      // The title-stamp fallback below is preserved for any pre-B1
      // marker still in sessionStorage that lacks a variantId.
      //
      // F1 budget (operator-locked, 2026-02 patch B1.F1, widened
      // 2026-02-15 by CWS-CV): the loop is sized for a realistic
      // LLM call. CWS-CV widens the budget from 30 × 3 s = 90 s
      // to 40 × 3 s = 120 s so it matches the backend
      // POLLING_HARD_CAP_MS (≈ 120 s) — the audit identified the
      // pre-CWS-CV 90 s ceiling as a recovery-exhaustion source
      // when the LLM tail exceeded 90 s. The marker's 10-minute
      // TTL (`INFLIGHT_RECOVERY_WINDOW_MS`) is still checked each
      // iteration so an older marker that has aged past its
      // budget exits promptly without burning the full window.
      if (marker.variantId) {
        const B1_POLL_TRIES = 40;
        const B1_POLL_DELAY_MS = 3000;
        let row = null;
        for (
          let attempt = 0;
          attempt < B1_POLL_TRIES && !cancelled;
          attempt += 1
        ) {
          if (attempt > 0) {
            if (!cancelled) setRecoveryStatus('recovering');
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, B1_POLL_DELAY_MS));
            if (cancelled) return;
            // Marker expired mid-loop — abandon the recovery so a
            // stale marker can't pin the banner for the full 90 s.
            if (isInflightMarkerExpired(marker)) {
              clearInflightCvMarker(primaryCv.user_id, jobId);
              if (!cancelled) setRecoveryStatus('exhausted');
              return;
            }
          }
          try {
            // eslint-disable-next-line no-await-in-loop
            row = await apiFetch(`/api/cv-variants/${marker.variantId}`);
          } catch {
            row = null;
          }
          if (cancelled) return;
          if (!row) break;
          const status = (row.run_status || 'complete').toLowerCase();
          if (status === 'complete') break;
          if (status === 'failed') {
            // Calm retry — clear the marker, surface advisory copy.
            clearInflightCvMarker(primaryCv.user_id, jobId);
            if (!cancelled) setRecoveryStatus('failed');
            return;
          }
          // queued | running — keep polling.
          if (!cancelled) setRecoveryStatus('recovering');
        }
        if (cancelled) return;
        if (row && (row.run_status || 'complete').toLowerCase() === 'complete') {
          // Hydrate through the SAME path as sync success. H1/H2
          // compliance pin: no parallel sparse setter.
          const recoveredVariantId = row.id;
          const recoveredVariantName = row.variant_name || marker.title || 'Tailored Variant';
          setGeneratedResult({
            variant_id:         recoveredVariantId,
            variant_name:       recoveredVariantName,
            generated_text:     row.content_markdown,
            recovered_inflight: true,
          });
          // F2 parity (operator-locked, 2026-02 patch B1.F2): bind
          // the variant as the active selection so the URL-write
          // effect at L364 stamps `?source_cv_type=cv_variant&
          // source_cv_id=<id>`. Without this, a subsequent refresh
          // would re-run the marker recovery loop instead of the
          // URL-bound rehydrate (which is the canonical fast path
          // for already-complete variants). Mirrors the legacy
          // title-stamp branch below at L657-661.
          setSelectedCv({
            type: 'cv_variant',
            id:   recoveredVariantId,
            name: recoveredVariantName,
          });
          clearInflightCvMarker(primaryCv.user_id, jobId);
          if (!cancelled) setRecoveryStatus('recovered');
          // F2 parity — calm restore toast, matching the title-
          // stamp branch's UX. The variantId branch was silent
          // pre-B1.F2 which surprised users on tab-restore.
          toast.success('We restored your generated CV.');
          // F2 parity — idempotent finalize-charge probe. The
          // server guards on `charged_at` and returns the same
          // canonical shape on replay; safe for already-charged
          // rows. If the row never reached finalize-charge before
          // the client disconnected (rare — tab closed between
          // backend's `complete` patch and client's POST), this
          // probe will run it now and surface the receipt block.
          try {
            const charge = await apiFetch(
              `/api/cv-variants/${recoveredVariantId}/finalize-charge`,
              { method: 'POST', body: { job_id: jobId } },
            );
            if (!cancelled && charge) {
              setGeneratedResult({
                variant_id:         recoveredVariantId,
                variant_name:       recoveredVariantName,
                generated_text:     row.content_markdown,
                recovered_inflight: true,
                ...charge,
              });
            }
            // RC 1.52 Phase 3A.4 — recovery path was previously
            // missing the post-finalize credits refresh, so the
            // wallet pill stayed stale until the 60s staleness
            // window or a hard reload. Trigger it here so the UI
            // matches server state immediately after recovery.
            // RC 1.53 Phase 4.2.1.1.b — awaited for consistency with
            // the happy-path stabilization chain.
            if (typeof credits?.refresh === 'function') {
              try { await credits.refresh(); } catch { /* noop */ }
            }
            // Recovery just completed a real dispatch — start the
            // same cooldown the happy path uses so the user can't
            // immediately re-fire Generate while the wallet syncs.
            try {
              startCooldown(cvVariantCooldownKey(primaryCv.user_id, jobId));
            } catch { /* noop */ }
          } catch (err) {
            // Fail-open — variant remains visible without billing
            // receipt. The 409/422 branches (B1 finalize-charge
            // gate) cannot fire here because we only reach this
            // block on `run_status === 'complete'`.
            // eslint-disable-next-line no-console
            console.warn(
              '[Workspace CvSection] B1 recovered finalize-charge probe failed:',
              err,
            );
          }
          return;
        }
        // Id-driven probe could not confirm completion → fall
        // through to the legacy title-scan as a last resort. This
        // is rare in practice (only fires if `/api/cv-variants/{id}`
        // is unreachable for the full 90 s window).
      }

      // Legacy title-stamp probe — preserved for pre-B1 markers
      // and as a last-resort fallback when the id-driven probe
      // could not confirm completion.
      const POLL_TRIES = 4;       // 0, 2s, 4s, 6s — total 6s cap
      const POLL_DELAY_MS = 2000;
      let found = null;
      for (let attempt = 0; attempt < POLL_TRIES && !cancelled; attempt += 1) {
        if (attempt > 0) {
          if (!cancelled) setRecoveryStatus('recovering');
          await new Promise((resolve) => setTimeout(resolve, POLL_DELAY_MS));
          if (cancelled) return;
        }
        // eslint-disable-next-line no-await-in-loop
        found = await findVariantByTitleStamp(marker.title);
        if (cancelled) return;
        if (found) break;
        if (attempt === 0) {
          // First miss — surface the recovering banner before
          // we wait for the next attempt.
          if (!cancelled) setRecoveryStatus('recovering');
        }
      }
      if (cancelled) return;
      if (!found) {
        // Retries exhausted; clear the marker so a future remount
        // doesn't loop and surface advisory inline copy.
        clearInflightCvMarker(primaryCv.user_id, jobId);
        if (!cancelled) setRecoveryStatus('exhausted');
        return;
      }
      // From here on out, we found a row — the legacy guard branches
      // run unchanged.

      // ── JOB-SCOPING GUARDS (workspace-only belt-and-braces) ──────
      // (a) base_cv_id must match the current primary CV.
      if (found.base_cv_id !== primaryCv.id) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Workspace CvSection] discarding recovered variant: base_cv_id mismatch',
          { variantBaseCv: found.base_cv_id, primaryCv: primaryCv.id },
        );
        clearInflightCvMarker(primaryCv.user_id, jobId);
        return;
      }
      // (b) idempotency_key must match the cached key (when present).
      const cachedKey = peekIdempotencyKey(
        primaryCv.user_id, jobId, 'cv_variant',
      );
      const variantKey = found?.content_json?.idempotency_key || null;
      if (cachedKey && variantKey && cachedKey !== variantKey) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Workspace CvSection] discarding recovered variant: idempotency_key mismatch',
        );
        clearInflightCvMarker(primaryCv.user_id, jobId);
        return;
      }

      const variantId = found.id || found.variant_id;
      const variantName = found.variant_name || found.title || marker.title;
      if (!variantId || !variantName) return;
      if (cancelled) return;

      // RC 1.50 R.2-fix — fetch the row to surface `content_markdown`
      // for the inline preview. The recovered-inflight path didn't
      // previously load the markdown, so an inline preview would
      // remain blank until the URL hydrate effect re-fetched. Read
      // is owner-scoped, idempotent, and unrelated to the
      // chargeable surface.
      let recoveredMarkdown = null;
      try {
        const fullRow = await apiFetch(`/api/cv-variants/${variantId}`);
        if (fullRow && typeof fullRow === 'object') {
          recoveredMarkdown = fullRow.content_markdown
            || (fullRow.content_json && fullRow.content_json.generated_text)
            || null;
        }
      } catch {
        // Fail-open — the URL-bound hydrate effect will retry.
      }
      if (cancelled) return;

      // Bind the recovered variant. Selection switches to the variant
      // (mirrors wizard L1772-1778).
      setGeneratedResult({
        variant_id: variantId,
        variant_name: variantName,
        recovered_inflight: true,
        generated_text: recoveredMarkdown,
      });
      setSelectedCv({
        type: 'cv_variant',
        id: variantId,
        name: variantName,
      });
      if (!cancelled) setRecoveryStatus('idle');
      clearInflightCvMarker(primaryCv.user_id, jobId);
      toast.success('We restored your generated CV.');

      // Idempotent finalize-charge probe (server returns the persisted
      // shape on replay — see backend routes/cv_variants.py:1296
      // `charged_at` guard). No double-charge risk.
      try {
        const charge = await apiFetch(
          `/api/cv-variants/${variantId}/finalize-charge`,
          { method: 'POST', body: { job_id: jobId } },
        );
        if (!cancelled && charge) {
          setGeneratedResult({
            variant_id: variantId,
            variant_name: variantName,
            ...charge,
          });
        }
        // RC 1.52 Phase 3A.4 — title-stamp recovery path also needs
        // an explicit credits refresh, mirroring the B1 id-driven
        // branch above. Without this the wallet pill stayed stale
        // until the 60s staleness window expired.
        // RC 1.53 Phase 4.2.1.1.b — awaited for consistency.
        if (typeof credits?.refresh === 'function') {
          try { await credits.refresh(); } catch { /* noop */ }
        }
        try {
          startCooldown(cvVariantCooldownKey(primaryCv.user_id, jobId));
        } catch { /* noop */ }
      } catch (err) {
        // Fail-open — variant remains visible.
        // eslint-disable-next-line no-console
        console.warn(
          '[Workspace CvSection] recovered finalize-charge probe failed:',
          err,
        );
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, primaryCv?.user_id, primaryCv?.id]);

  // ── RC 1.49.7 Slice 2 — URL-driven state retention (operator P0,
  //    2026-02). Pre-Slice-2 behaviour: refreshing the CV section
  //    (or returning to it from Cover Letter) wiped `generatedResult`
  //    because state was local-only — the URL had
  //    `source_cv_type=cv_variant&source_cv_id=<id>` but nothing
  //    rehydrated the success card. Operator soak surfaced this as
  //    "the generated CV disappeared" (defects 2 + 3).
  //
  //    This effect performs a READ-ONLY GET on `/api/cv-variants/<id>`
  //    when:
  //      • URL says we're already bound to a variant
  //      • generatedResult is null (not currently bound)
  //      • not currently generating
  //      • no in-flight marker (the recovery effect above owns that
  //        path; we never race it)
  //    The endpoint is owned-by-user only and returns 404 otherwise
  //    (cv_variants.py:1180-1192). NEVER calls `/generate`. NEVER
  //    mints an idempotency key. Zero charge.
  //
  //    RUN_ISOLATION compatibility: hydration is gated on URL-bound
  //    `(source_cv_type, source_cv_id)`. This is "current run state"
  //    keyed to the URL, not historical re-attachment — same shape
  //    as Fit hydrating from `/best-fit` for the URL-bound source
  //    (operator-accepted in C7, 2026-02-08).
  const sourceCvTypeUrl = searchParams.get('source_cv_type');
  const sourceCvIdUrl = searchParams.get('source_cv_id');
  useEffect(() => {
    if (!jobId || !primaryCv?.user_id || !primaryCv?.id) return undefined;
    if (sourceCvTypeUrl !== 'cv_variant') return undefined;
    if (!sourceCvIdUrl) return undefined;
    if (generatedResult) return undefined;
    if (generating) return undefined;
    // Defer to in-flight recovery if a marker exists.
    if (readInflightCvMarker(primaryCv.user_id, jobId)) return undefined;

    let cancelled = false;
    setRestoring(true);
    (async () => {
      try {
        const row = await apiFetch(`/api/cv-variants/${sourceCvIdUrl}`);
        if (cancelled || !row) return;
        // Cross-job guard — variant must belong to this primaryCv.
        if (row.base_cv_id && row.base_cv_id !== primaryCv.id) {
          // eslint-disable-next-line no-console
          console.warn(
            '[Workspace CvSection] URL-bound variant base_cv_id mismatch — ignored.',
          );
          return;
        }
        const variantId = row.id || sourceCvIdUrl;
        const variantName =
          row.variant_name || row.title || 'Tailored Variant';
        // ── U-13.CV.PATCH-B (2026-02-15) — race-shape parity. ────
        // OPERATOR-LOCKED. PRE-PATCH-B this setter mapped billing
        // telemetry from `content_json.*` (cj.charged / cj.reason /
        // cj.delta / cj.usage_consumed / etc.) — but those fields
        // are NOT persisted in content_json. They live as TOP-LEVEL
        // columns (`charged_at`, `charge_reason`, `cv_sub_score_at_
        // finalize`) plus the delta inside `optimisation_artifacts.
        // analytics_metadata.variant_uplift_delta`. The Effect-A
        // path (refetchVariantFromUrl, L733-841) was patched in
        // U-13 to use the correct sources; Effect B was left on
        // the legacy cj.* mapping.
        //
        // Symptom (operator live-smoke 2026-02-15): on Pack open
        // both effects fire in parallel. Effect A's rich state
        // lands first → card shows the proper "Material improvement
        // achieved — charged. Δ +X pts." line. ~1-2s later Effect
        // B's fetch resolves and OVERWRITES with all-null billing
        // fields → legacy "Billing details — —" fallback fires.
        // User observed the receipt disappear within seconds of
        // Pack load.
        //
        // Fix: read from the SAME row sources Effect A uses
        // (top-level columns + optimisation_artifacts.analytics_
        // metadata). Whichever effect wins the race now produces
        // an identical rich state — no overwrite-induced flicker.
        // `cj` is preserved purely for the future-proofing
        // fallback on `generated_text` (the canonical source is
        // `row.content_markdown`).
        const cj = row.content_json || {};
        const _optArtefactsB = (row.optimisation_artifacts && typeof row.optimisation_artifacts === 'object')
          ? row.optimisation_artifacts : {};
        const _analyticsMetaB = (_optArtefactsB.analytics_metadata && typeof _optArtefactsB.analytics_metadata === 'object')
          ? _optArtefactsB.analytics_metadata : {};
        const _cvSubVariantB = row.cv_sub_score_at_finalize
          ?? cj.cv_sub_score_variant
          ?? null;
        const _cvSubSourceB  = _analyticsMetaB.source_cv_sub_score ?? null;
        let _deltaB = _analyticsMetaB.variant_uplift_delta;
        if (_deltaB == null
            && _cvSubVariantB != null
            && _cvSubSourceB != null) {
          const _d = Number(_cvSubVariantB) - Number(_cvSubSourceB);
          _deltaB = Number.isFinite(_d) ? _d : null;
        }
        setGeneratedResult({
          variant_id:                 variantId,
          variant_name:               variantName,
          reused:                     true,
          // Billing telemetry — sourced from row top-level columns
          // + optimisation_artifacts.analytics_metadata. Matches
          // Effect A (refetchVariantFromUrl) exactly.
          charged:                    !!row.charged_at,
          reason:                     row.charge_reason || null,
          delta:                      _deltaB,
          tolerance_band:             5,
          cv_sub_score_variant:       _cvSubVariantB,
          cv_sub_score_source:        _cvSubSourceB,
          // Improvement explainers (these MAY live in cj on legacy
          // rows). Pass through whatever is present.
          fit_summary:                cj.fit_summary ?? row.fit_summary ?? cj,
          cv_verdict:                 cj.cv_verdict ?? row.cv_verdict,
          improvement_map:            cj.improvement_map ?? row.improvement_map,
          defensibility_map:          cj.defensibility_map ?? row.defensibility_map,
          optimisation_artifacts:     row.optimisation_artifacts ?? cj.optimisation_artifacts,
          // RC 1.50 R.2 — generated text for inline preview parity
          // with Cover Letter. CANONICAL column is `content_markdown`.
          generated_text:
            cj.generated_text
            ?? row.content_markdown
            ?? row.generated_text,
          // `usage_consumed` / `charge_percentage` are NOT row-
          // persisted (first-call response only) — intentionally
          // omitted, matching Effect A. The legacy fallback at
          // L2573-2586 requires ALL of reason+delta+usage_consumed
          // null to fire; with reason set, the fallback stays
          // suppressed.
        });
      } catch (err) {
        // 404 (variant deleted) or transient — fail-closed: leave
        // generatedResult null so the section renders the empty
        // state with a baseline guard rather than a broken card.
        // eslint-disable-next-line no-console
        console.warn(
          '[Workspace CvSection] URL-bound variant rehydrate failed:',
          err,
        );
      } finally {
        // RC 1.49.7 Layer-A* UX — clear the restoring banner
        // unconditionally (PackSection-parity).
        setRestoring(false);
      }
    })();
    return () => { cancelled = true; };
    // generatedResult / generating intentionally NOT in deps — we
    // only want this effect to fire on URL or primaryCv changes,
    // not whenever the success card binds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, primaryCv?.user_id, primaryCv?.id, sourceCvTypeUrl, sourceCvIdUrl]);

  // ── Pre-flight credits gate (toast-only Phase 1) ─────────────────
  const ensureCreditsAvailable = (actionType, actionLabel) => {
    if (creditsLoading) return false;
    const cost = getActionCost(usageCosts, actionType);
    if (cost == null || cost === 0) return true;
    const available =
      Number(credits?.includedUsageRemaining || 0)
      + Number(credits?.extraUsageRemaining || 0);
    if (available >= cost) return true;
    toast.error(`Not enough Credits for ${actionLabel}`, {
      description: `${available} available · ${cost} needed. Open Plan to add Credits.`,
      action: {
        label: 'View plan',
        onClick: () => {
          try { window.location.assign('/account?tab=plan'); } catch { /* noop */ }
        },
      },
    });
    return false;
  };

  // ── Generate / Regenerate core dispatch ──────────────────────────
  //
  // RC 1.49.7 C5 — pre-flight guards (BaselineRefreshModal, Soft
  // reject-gate, High-CV Value Guard) are ported inline from the
  // wizard. The centralised `_runGenerateInner` handles the
  // chargeable POST + finalize-charge pair, parameterised so every
  // entry point (default, guard confirm handlers) funnels through
  // a single audit-safe code path. Mirrors the wizard's
  // `_runGenerateInner` contract at JobApplicationWizard.jsx L2389.
  //
  //   refreshDecision   → 'refreshed' | 'declined' | null
  //                       Forwarded to finalize-charge. Only set by
  //                       the BaselineRefreshModal confirm handlers.
  //   forceGenerate     → boolean
  //                       RC 1.48.3 soft-override. Set by the reject-
  //                       gate confirm handler AND by the pack-
  //                       canonical pre-emptive bypass.
  //   highCvConfirmed   → boolean
  //                       RC 1.48.6 audit-only. Forwarded as
  //                       `high_cv_generate_confirmed` to backend.
  //                       Set ONLY by the High-CV guard confirm
  //                       handler. The score-driven charge gate is
  //                       NEVER influenced by this flag (it's audit
  //                       telemetry only).
  const _runGenerateInner = async ({
    refreshDecision = null,
    forceGenerate = false,
    highCvConfirmed = false,
    cvOptions = null,
  } = {}) => {
    const variantTitle = mintCvVariantTitle({ role: job?.title });
    // RC 1.51 follow-up A — resolve CV generation options. The
    // GenerateCvOptionsSheet's Confirm handler resolves the chosen
    // style preset to its (template_type, tone_type, length_preference)
    // trio + the additional_cv_context textarea before invoking us.
    // If a guard re-entry path forgot to pass `cvOptions` through, we
    // fall back to GEN_CV_DEFAULTS so the chargeable POST never sees
    // an undefined required field.
    const opts = cvOptions || GEN_CV_DEFAULTS;

    // RC 1.55 Phase Z.2 (operator-locked, 2026-05-13) — Prefer the
    // user-intent SNAPSHOT captured at modal-confirm time over the
    // mutable `job` / `primaryCv` parent closure. The snapshot is
    // attached to `cvOptions._snapshot` by `handleOptionsConfirm`
    // (single source of truth) and threaded through every guard's
    // `captured.cvOptions`. The legacy `job.description` /
    // `primaryCv.id` closure reads remain as fallbacks so a missing-
    // snapshot codepath (e.g. hot-reload mid-flow, or a future guard
    // that doesn't yet thread the snapshot) doesn't hard-break.
    const _snapshot = (cvOptions && cvOptions._snapshot) || null;
    // RC 1.55 Phase Z.4 (operator-locked, 2026-05-13 PM) —
    // job_text resolution provenance, captured for the
    // `X-Ndor-Job-Text-Source` telemetry header. The four mutually
    // exclusive outcomes mirror the Z.2 fallback chain:
    //   • 'snapshot' — the Z.2 user-intent snapshot resolved.
    //   • 'closure'  — snapshot missing/empty; the mutable
    //                  `job.description` closure resolved.
    //   • 'refetch'  — both above empty; the Z.2 G3 read-only
    //                  `GET /api/jobs/{id}` refetch resolved.
    //   • 'none'     — every fallback returned empty; the helper
    //                  calm-aborts and NO POST fires. Logged here
    //                  for completeness; the backend should never
    //                  see this value because the abort returns
    //                  before dispatch.
    // Read-only — never gates billing / scoring / idempotency.
    let _jobTextSource = 'none';
    let _resolvedJobText;
    if (typeof _snapshot?.job_text === 'string' && _snapshot.job_text.trim()) {
      _resolvedJobText = _snapshot.job_text;
      _jobTextSource = 'snapshot';
    } else if (typeof job?.description === 'string' && job.description.trim()) {
      _resolvedJobText = job.description;
      _jobTextSource = 'closure';
    } else {
      _resolvedJobText = '';
    }
    const _resolvedBaseCvId = _snapshot?.base_cv_id || primaryCv?.id;
    const _resolvedBaseCvUserId =
      _snapshot?.base_cv_user_id || primaryCv?.user_id;

    // RC 1.55 Phase Z.2 G3 — Defence-in-depth pre-dispatch refetch.
    // If BOTH the snapshot and the live closure produce an empty
    // `job_text`, perform ONE read-only canonical refetch of the
    // job row. If that also fails, abort with a calm toast — DO
    // NOT dispatch (the backend adapter at
    // `core/run_adapters/cv_variant.py:120` would reject with
    // `ADAPTER_INPUT_ERROR` anyway; this avoids the user-visible
    // 400 + the "did anything get charged?" anxiety). This is the
    // exact failure mode the operator hit on jobs `66ebdeb6` /
    // `f383c2f1` (short 214–269 char descriptions in the upstream-
    // oil-and-gas family) on 2026-05-13.
    if (!_resolvedJobText) {
      try {
        const fresh = await apiFetch(`/api/jobs/${jobId}`);
        if (typeof fresh?.description === 'string'
            && fresh.description.trim()) {
          _resolvedJobText = fresh.description;
          // RC 1.55 Phase Z.4 — refetch fallback succeeded.
          _jobTextSource = 'refetch';
        }
      } catch (_e) {
        // Fall through to the empty-text abort below — the user
        // sees the calm toast, not a network-error toast.
      }
    }
    if (!_resolvedJobText) {
      toast.error(
        "We couldn't read the full job description."
        + ' Please refresh and try again — no Credits charged.',
        { duration: 9000 },
      );
      // The caller (`_dispatchWithCatches` / `handleRejectConfirm`
      // / `handleBaselineRefresh` / `handleBaselineDecline`) flips
      // `setGenerating(false)` in its own `finally`. We just refuse
      // to dispatch — no marker, no idempotency key consumed, no
      // POST.
      return null;
    }

    // Marker BEFORE the LLM call — so a tab close mid-Generate is
    // recoverable on next mount.
    writeInflightCvMarker(_resolvedBaseCvUserId, jobId, variantTitle);
    // RC 1.57 Generation Session Guard — get-or-mint the session-
    // scoped idempotency key for THIS (user, job, 'cv_variant')
    // tuple. Pre-RC 1.57 the key was minted ad-hoc and cleared
    // immediately after finalize-charge — leaving a window in which
    // a tab-switch / remount could mint a fresh key and dispatch a
    // second chargeable variant. The session guard keeps the key
    // alive through finalize-charge AND the post-success hydration
    // cycle, releasing only after `markHydrationStable` fires.
    const _cv_session = getOrMintSession(
      _resolvedBaseCvUserId, jobId, 'cv_variant',
    );
    const idempotencyKey = _cv_session.idempotencyKey;

    actionTrace('cv_variant', 'dispatch_start', {
      jobId, idempotencyKey, baseCvId: _resolvedBaseCvId, title: variantTitle,
    });
    setProgressLabel('dispatching');
    const variant = await controllerDispatch({
      jobId,
      actionCode: 'cv_variant',
      idempotencyKey,
      // The controller's no-op-on-in-flight guard returns the
      // existing promise if a `cv_variant` operation is already in
      // flight for this job — defence-in-depth on top of the
      // section's own `loading` state. The polling helper itself is
      // unchanged (Phase 4.2 contract — wrapped, not rewritten).
      //
      // Phase 4.2.1 — `callbacks` are passed through the adapter
      // into the polling helper's `onRunStatus` so the controller
      // emits `operation:progress` for every backend run_status
      // transition. The section's `setProgressLabel` listener
      // surfaces the latest label to the status-copy area.
      run: async (callbacks) => cvVariantAdapter.run({
        callbacks: {
          ...callbacks,
          onProgress: (status, label) => {
            try { setProgressLabel(label || status); } catch { /* noop */ }
            try { callbacks?.onProgress?.(status, label); } catch { /* noop */ }
          },
        },
        // CWS-CV (operator-locked, 2026-02-15) — stamp the
        // variantId into the inflight marker the moment the
        // async_202 envelope returns it. Closes the audit-
        // identified pre-runId marker race where a tab switch
        // between the initial `writeInflightCvMarker(...)` (no
        // variantId, L1315) and the post-resolve upgrade
        // (variantId, L1445) left the marker without an id —
        // forcing mount recovery into the fragile title-stamp
        // fallback. The post-resolve upgrade below at L1445 is
        // RETAINED for the sync_200 / replay_200 paths (which
        // never invoke onRunIdReady) and as a defence-in-depth
        // re-write of the same row.
        onRunIdReady: (resolvedVariantId) => {
          try {
            writeInflightCvMarker(
              _resolvedBaseCvUserId,
              jobId,
              variantTitle,
              resolvedVariantId,
            );
          } catch { /* noop */ }
        },
        // RC 1.55 Phase Z.4 — forensic telemetry. The header
        // captures which fallback in the Z.2 chain produced the
        // dispatched `job_text`. READ-ONLY: never read by billing
        // / scoring / idempotency. The polling helper itself
        // adds `X-Ndor-Dispatch-Surface` and `X-Ndor-Bundle-Version`.
        telemetryHeaders: {
          'X-Ndor-Job-Text-Source': _jobTextSource,
        },
        requestBody: {
          base_cv_id:        _resolvedBaseCvId,
          job_id:            jobId,
          job_text:          _resolvedJobText,
          title:             variantTitle,
          template_type:     opts.template_type,
          tone_type:         opts.tone_type,
          length_preference: opts.length_preference,
          additional_cv_context: opts.additional_cv_context || '',
          // RC 1.48.3 — soft-override decision (audit only).
          force_generate:              !!forceGenerate,
          // RC 1.48.6 — audit-only; charge gate is score-driven.
          high_cv_generate_confirmed:  !!highCvConfirmed,
          // RC 1.49.1 P0-FIN — financial idempotency for retries.
          // Mirrored at envelope level by apiPost below.
          idempotency_key: idempotencyKey,
        },
        apiPost: async (path, opts2) => apiFetch(path, {
          ...opts2,
          body: {
            ...(opts2 && opts2.body ? opts2.body : {}),
            idempotency_key: idempotencyKey,
          },
        }),
        apiGet: (path, opts2) => apiFetch(path, opts2),
        onRunStatus: (s) => {
          // RC 1.52 Phase 3A.2 — map polling lifecycle to the same
          // banner copy the legacy `onRecoveryState` produced so QA
          // snapshots / user expectations are preserved.
          if (s === 'running') {
            toast.info(
              "This is taking longer than usual. We're checking your results...",
            );
          } else if (s === 'failed') {
            toast.warning(
              "We're unable to confirm the result reached you."
              + ' Before clicking Generate again, please open'
              + ' Documents → CV Variants — your document may already'
              + ' be there.',
              { duration: 9000 },
            );
          }
        },
      }),
    });
    actionTrace('cv_variant', 'terminal_payload_received', {
      variantId: variant?.variant_id || variant?.id,
      contentLen: (variant?.generated_text || '').length,
      idempotent_replay: !!variant?.idempotent_replay,
      served_via: variant?.served_via || null,
    });
    // RC 1.53 Phase 4.2.1.1 — BILLING INTEGRITY REORDER.
    //
    // OLD ORDER (orphan-risk):
    //   dispatch → verifyAndHydrate → finalize-charge → setState
    //   ↑ If the hydration verifier threw a `recoverable=true`
    //     error here, the section's catch block surfaced a calm
    //     "still generating" toast and `return`ed — bypassing
    //     finalize-charge. The backend row persisted as
    //     `run_status='complete', charged_at=null` (orphan).
    //
    // NEW ORDER (operator-locked, 2026-05-12):
    //   dispatch → finalize-charge → verifyAndHydrate (UI only) → setState
    //   ↑ Hydration is a UI-freshness concern, NOT a billing gate.
    //     finalize-charge is replay-safe (server `charged_at` guard
    //     dedupes) and gated against incomplete rows (409/422 if
    //     the row is still queued/running/failed). So firing it
    //     immediately after the dispatch returns its terminal
    //     payload guarantees "successful persisted output → exactly
    //     one charge" with no path where billing can be silently
    //     skipped.
    const variantId = variant?.variant_id || variant?.id;
    if (!variantId) {
      throw new Error('Variant id missing in response.');
    }

    // RC 1.51 follow-up B1 — UPGRADE the marker to carry the durable
    // `variant_id`. A subsequent tab close + refresh drives recovery
    // deterministically via `GET /api/cv-variants/{variantId}`. Done
    // BEFORE finalize-charge so a crash mid-charge is recoverable
    // by the mount sweep (which probes the marker's variantId).
    writeInflightCvMarker(
      primaryCv.user_id, jobId, variantTitle, variantId,
    );

    // ─── STEP 1: finalize-charge (BEFORE any hydration / UI work) ──
    const finalizeBody = { job_id: jobId };
    if (refreshDecision) finalizeBody.refresh_decision = refreshDecision;
    setProgressLabel('finalizing');
    actionTrace('cv_variant', 'finalize_charge_request', {
      variantId, refresh_decision: refreshDecision || null,
    });
    const charge = await apiFetch(
      `/api/cv-variants/${variantId}/finalize-charge`,
      { method: 'POST', body: finalizeBody },
    );
    actionTrace('cv_variant', 'finalize_charge_response', {
      variantId,
      charged: !!charge?.charged,
      reused: !!charge?.reused,
      reason: charge?.reason || null,
      usage_consumed: charge?.usage_consumed ?? null,
    });

    // ─── STEP 2: hydration verifier (UI-freshness ONLY) ────────────
    // Runs AFTER finalize-charge so a verifier raise can NEVER
    // bypass billing. On `recoverable=true` we calmly fall back to
    // the terminal envelope's `generated_text` — the row is already
    // charged (or replay-safe), and the inline preview can rehydrate
    // on the next mount via the URL-bound rehydrate effect.
    setProgressLabel('hydrating');
    let verified = variant;
    try {
      verified = await verifyAndHydrateCvVariant(variant, {
        apiGet: (path, opts2) => apiFetch(path, opts2),
      });
      actionTrace('cv_variant', 'verified', {
        variantId,
        hydrated_from_authoritative: verified !== variant,
      });
    } catch (vErr) {
      // Recoverable verifier raise — billing has ALREADY been
      // finalised above. Surface a calm advisory and continue with
      // the terminal envelope; the URL-bound rehydrate effect will
      // refresh the preview on next mount.
      actionTrace('cv_variant', 'verifier_recoverable_post_charge', {
        variantId,
        code: vErr?.code || null,
        recoverable: !!vErr?.recoverable,
      });
      if (!vErr?.recoverable) throw vErr;
      verified = variant;
    }

    // ─── STEP 3: stabilization — defer session release ────────────
    // RC 1.57 Generation Session Guard — finalize-charge has
    // returned a terminal-success envelope. Mark the session as
    // `terminalSuccess` so any visibility / remount event over the
    // next 30 s reads the SAME idempotency key (no fresh charge),
    // but DO NOT clear the key yet — hydration is still landing.
    // `markHydrationStable` + `releaseSessionIfStable` fire below
    // after the whole post-success chain (state bind, URL write,
    // wallet refresh, cooldown) commits.
    markTerminalSuccess(primaryCv.user_id, jobId, 'cv_variant');
    clearInflightCvMarker(primaryCv.user_id, jobId);

    const variantName =
      verified?.variant_name || variant?.variant_name || variantTitle;
    setGeneratedResult({
      variant_id: variantId,
      variant_name: variantName,
      generated_text: verified?.generated_text || variant?.generated_text,
      ...charge,
    });
    {
      const next = new URLSearchParams(searchParams);
      if (next.get('last_variant_id') !== variantId) {
        next.set('last_variant_id', variantId);
        setSearchParams(next, { replace: true });
      }
    }
    setSelectedCv({
      type: 'cv_variant',
      id: variantId,
      name: variantName,
    });
    toast.success(
      charge?.reused
        ? 'Loaded from your history — no new Credits charged. Click Regenerate below to spend Credits on a fresh tailored CV.'
        : 'Tailored CV ready.',
    );
    // RC 1.53 Phase 4.2.1.1 — AWAIT the credits refresh so the
    // activity indicator stays in `stabilizing` ("Saving your tailored
    // CV…") until the wallet pill is actually re-read from the server.
    // The orchestrator's job is to re-read from authoritative state
    // (DB) after a chargeable action lands — never to re-dispatch the
    // LLM. Fail-open: if the refresh blips, we still proceed to
    // markComplete so the indicator does not get stuck forever.
    if (typeof credits?.refresh === 'function') {
      try { await credits.refresh(); } catch { /* noop */ }
    }
    actionTrace('cv_variant', 'credits_refresh_called', { variantId });
    try {
      controllerEmit(OPERATION_EVENT_TYPES.PACK_ARTEFACT_READY, {
        jobId,
        actionCode: 'cv_variant',
        artefactId: variantId,
        reused: !!charge?.reused,
      });
    } catch { /* noop */ }
    try {
      startCooldown(cvVariantCooldownKey(primaryCv?.user_id, jobId));
    } catch { /* noop */ }
    setProgressLabel(null);
    // RC 1.59 Slice 4 — invalidate the Documents list cache so the
    // freshly generated CV variant appears on the next visit to
    // `/documents?tab=variants` and on ApplicationsPage's variant
    // subload without a TTL wait. Also sweep the dashboard bundle
    // since the queue counter derives off the variant list. Belt-
    // and-braces — the legacy CustomEvent above is preserved for any
    // listener that depends on it; this call closes the same gap at
    // the canonical listCache layer.
    try { cacheInvalidate(['cv-variants:all', 'dashboard:bundle']); }
    catch { /* noop */ }
    // ─── STEP 4: signal terminal completion to the controller ──────
    // The activity indicator unmounts ONLY now — after finalize-
    // charge, hydration, credits refresh, cache-invalidation event
    // and URL/state writes have all landed. The orchestrator is
    // "first visible, last gone".
    try {
      lifecycleMarkComplete({ variantId, reused: !!charge?.reused });
    } catch { /* noop — controller may not have an active op */ }
    actionTrace('cv_variant', 'operation_closed', {
      variantId, reused: !!charge?.reused,
    });
    // RC 1.57 — hydration chain has fully committed. Mark stable
    // and release the session idempotency key. Subsequent
    // deliberate "Generate New Version" clicks now mint a FRESH
    // session key. Tab-switches / remounts inside the natural
    // session TTL window (10 min) still reuse the key, but with
    // `terminalSuccess + hydrationStable` set the helper releases
    // immediately so the next deliberate click can mint anew.
    markHydrationStable(primaryCv.user_id, jobId, 'cv_variant');
    releaseSessionIfStable(primaryCv.user_id, jobId, 'cv_variant');
  };

  // Continuation after the High-CV guard has cleared (either the
  // pre-flight skipped it, or the user clicked "Generate anyway").
  // Runs the remaining two pre-flights (baseline-status + pack probe)
  // and then fires the chargeable flow via `_runGenerateInner`.
  // RC 1.55 Phase Z.3 (operator-locked, 2026-05-13) — Shared
  // dispatch helper. Centralises the catch chain that previously
  // lived in `_generateAfterHighCv` so all four entry points
  // (normal sheet-confirm, high-CV-confirm, reject-confirm,
  // baseline-refresh/decline) share IDENTICAL error handling
  // without DRY violation. After the Phase Z.3 preflight-
  // reordering, NO entry point has any `apiFetch` between
  // modal-close and this dispatch — `_runGenerateInner` runs
  // in the next microtask following the user's confirm click.
  //
  // Strict-hold reminder: no backend / billing / scoring /
  // pricing / idempotency / finalize-charge / schema impact —
  // this is a refactor of the frontend orchestration.
  const _dispatchWithCatches = async ({
    refreshDecision = null,
    forceGenerate = false,
    highCvConfirmed = false,
    cvOptions = null,
  } = {}) => {
    try {
      return await _runGenerateInner({
        refreshDecision, forceGenerate, highCvConfirmed, cvOptions,
      });
    } catch (err) {
      // 409 BASELINE_VERSION_STALE → re-open the baseline guard.
      // Carry `forceGenerateHint` so a downstream re-dispatch
      // honours the pack-canonical bypass computed at preflight.
      if (
        err?.status === 409
        && err?.data?.detail?.code === 'BASELINE_VERSION_STALE'
      ) {
        setBaselineGuard({
          baseline_version: err.data.detail.baseline_version,
          current_version:  err.data.detail.current_version,
          highCvConfirmed,
          cvOptions,
          forceGenerateHint: forceGenerate,
          busy:             null,
        });
        return null;
      }
      // 409 analyse_fit_reject → open the soft reject-gate.
      if (
        err?.status === 409
        && err?.data?.detail?.error === 'analyse_fit_reject'
      ) {
        setRejectGuard({
          fit_score:       err.data.detail.fit_score ?? null,
          cv_sub_score:    err.data.detail.cv_sub_score ?? null,
          threshold:       err.data.detail.threshold ?? null,
          final_decision:  err.data.detail.final_decision ?? null,
          highCvConfirmed,
          cvOptions,
          busy:            false,
        });
        return null;
      }
      if (handleCreditsError(err, {
        action: 'Generate CV',
        refreshCredits: credits?.refresh,
      })) return null;
      // RC 1.52 Phase 3A.2 D-3 — recoverable still-running. Calm
      // toast rather than a hard error / idle reset.
      if (err && err.recoverable === true) {
        toast.info(
          'Your CV variant is still generating in the background.'
          + ' Please check Documents → CV Variants in a minute.',
          { duration: 9000 },
        );
        return null;
      }
      // RC 1.52 Phase 3A.2 — friendlier server-error UX. Backend
      // invariants guarantee no charge fires on a failed run.
      const status = err?.status;
      if (typeof status === 'number' && status >= 500) {
        toast.error(
          "Sorry — we couldn't generate your CV just now."
          + " Your credits weren't charged. Please try again in"
          + ' a moment, or contact support if this keeps happening.',
          { duration: 9000 },
        );
      } else {
        toast.error(err?.message || 'Failed to generate CV.');
      }
      // RC 1.55 Phase Z.5 (operator-locked, 2026-05-13 PM) — Stale
      // inflight marker hygiene on non-recoverable terminal error.
      // The earlier arms (409 BASELINE_VERSION_STALE, 409
      // analyse_fit_reject, credits, recoverable=true) all intend
      // a re-dispatch via the guard flow OR a real backend run
      // that should be recoverable on tab-return, so we DELIBERATELY
      // leave the marker intact in those branches. This arm — the
      // hard 4xx (non-409 / non-recoverable) and 5xx fallthrough —
      // means the user has been shown a definitive error toast and
      // no further automatic resolution is expected. Clearing the
      // marker here prevents the "Generating your tailored CV…"
      // recovery card from being stuck after a hard failure, which
      // was observed after the Phase Z.4 telemetry exposed the
      // cv_variant adapter's missing async_202 branch
      // (`backend/core/run_adapters/cv_variant.py` pre-Z.5).
      // Strict-rail: no backend / billing / scoring / idempotency
      // impact — only erases the local recovery hint.
      try {
        if (primaryCv?.user_id && jobId) {
          clearInflightCvMarker(primaryCv.user_id, jobId);
        }
      } catch { /* noop — marker hygiene must never crash */ }
      return null;
    } finally {
      // RC 1.53 Phase 4.2.1.1 — defensive controller resync. If
      // the op is still in `dispatching` / `polling` / `stabilizing`
      // when we exit (e.g. early-returned on a 409 / credits / toast
      // path), surface a controlled failure so the activity
      // indicator unmounts. `markFailed` is idempotent and a no-op
      // when the op is already in a terminal state.
      try { lifecycleMarkFailed({ message: 'generation_path_exited' }); }
      catch { /* noop */ }
    }
  };

  // ── Top-level Generate handler (the user's click entry-point) ───
  //
  // RC 1.51 follow-up A — Generate-button click no longer fires
  // the chargeable flow directly. Instead, it opens the
  // GenerateCvOptionsSheet. The sheet's onConfirm captures the
  // user's chosen style preset + additional context and forwards
  // them through the high-CV / baseline / reject guard chain via
  // `handleOptionsConfirm` → `_dispatchWithCatches`. Pre-flight
  // gates (no Source CV / no description / not enough Credits)
  // run here BEFORE the sheet opens so we never surface the
  // configuration UI on a flow that can't possibly succeed.
  const handleGenerate = async () => {
    if (!primaryCv?.id) {
      toast.error('No Source CV. Upload one in Documents → CVs.');
      return;
    }
    if (!job?.description?.trim()) {
      toast.error('Job has no description text.');
      return;
    }
    if (!ensureCreditsAvailable('cv_improve', 'Generate CV')) return;
    // ── U-5.CV.A — Regenerate confirm gate (2026-02-15) ──────────
    //
    // OPERATOR-LOCKED. Mirrors U-3.IP. Closes Race #1 and Race #10
    // from the cross-tool race audit: when a tailored CV is
    // ALREADY bound (from the U-2.CV mount fallback, marker
    // recovery, or a prior in-session generation), a fresh Generate
    // click would otherwise:
    //   • reuse the active session-guard idempotency key, OR
    //   • fire a fresh dispatch the user did not consciously authorise.
    //
    // With the gate, when `generatedResult?.variant_id` is truthy,
    // the click opens an inline confirm dialog. "Keep existing"
    // dismisses without dispatch. "Regenerate (≈ N Credits)"
    // FIRST resets the session-guard so the dispatch mints a fresh
    // idempotency key, THEN opens the options sheet (existing
    // chargeable flow). First-time generation (no result bound)
    // bypasses the dialog cleanly.
    //
    // Read-only gate; no charge, no dispatch, no LLM.
    if (generatedResult?.variant_id) {
      setRegenerateConfirm({ source: 'cv_generate' });
      return;
    }
    setOptionsOpen(true);
  };

  // ── RC 1.51 follow-up A — Sheet-confirm bridge to chargeable flow.
  // The sheet already resolves the chosen style preset to its
  // (template_type, tone_type, length_preference) trio + the
  // additional_cv_context textarea (see GenerateCvOptionsSheet
  // L198-214). We capture that exact shape into `pendingCvOptions`
  // (so guard re-entry handlers can reuse it without reopening the
  // sheet) and then run the high-CV pre-flight, which fans out to
  // baseline / reject / chargeable POST as before.
  const handleOptionsConfirm = async (cvOptions) => {
    setOptionsOpen(false);
    // RC 1.53 Phase 4.3 — Double-click race fix. Lock the main
    // Generate button the instant the sheet confirms. The async
    // pre-flights below (baseline-status, pack-canonical) all run
    // BEFORE `_dispatchWithCatches` flips `generating=true`.
    // Without this flag the user can double-click Generate during
    // that window and trigger a second chargeable run (confirmed in
    // operator soak — 18 credits charged for two runs from a single
    // intent).
    setSubmittingCv(true);

    // RC 1.55 Phase Z.2 (operator-locked, 2026-05-13) — Snapshot
    // the chargeable-dispatch inputs at user-intent time. The
    // closure-staleness defect (see operator's 2026-05-13 gold-
    // eye audit) lets `job.description` become falsy mid-flight
    // when a `visibilitychange`/`popstate`/parent-rerender lands
    // between modal-close and `controllerDispatch`. By capturing
    // the user-intent inputs into a frozen object NOW and threading
    // it through every guard's `captured.cvOptions`, the eventual
    // `_runGenerateInner` reads from a stable source instead of
    // re-reading the mutable parent closure. Strict hold: no
    // backend / billing / scoring / idempotency / schema impact —
    // this is a frontend-only fault-path defence.
    //
    // The snapshot is informational (preferred read) — the
    // fallback chain inside `_runGenerateInner` keeps the legacy
    // `job.description` read available in case `_snapshot` is
    // absent (e.g. a hot-reload mid-flow drops the closure).
    const _snapshotJobText = (typeof job?.description === 'string'
                              && job.description.trim())
      ? job.description
      : null;
    const _snapshot = {
      job_text:        _snapshotJobText,
      base_cv_id:      primaryCv?.id ?? null,
      base_cv_user_id: primaryCv?.user_id ?? null,
      job_title:       job?.title ?? null,
      captured_at:     Date.now(),
    };
    const cvOptionsWithSnapshot = { ...cvOptions, _snapshot };

    // RC 1.55 Phase Z.3 (operator-locked, 2026-05-13) — Preflight
    // reordering. ALL non-chargeable preflight checks now run
    // BEFORE any confirmation modal (high-CV, reject-gate,
    // baseline-stale). Operator design intent: once the user
    // clicks Generate Anyway / Confirm on any modal, dispatch is
    // a straight shot — NO further `apiFetch` awaits. This
    // collapses the closure-staleness window observed pre-Z.2 and
    // gives the four entry points symmetric `_dispatchWithCatches`
    // wiring.
    //
    // Preflight 1 — baseline-status. If stale, open BaselineGuard
    // BEFORE the high-CV modal so the user sees the freshness
    // prompt first (correct precedence). Fail-open on network
    // error: the backend 409 BASELINE_VERSION_STALE catch inside
    // `_dispatchWithCatches` is the defence-in-depth retry path.
    try {
      const status = await apiFetch(`/api/jobs/${jobId}/baseline-status`);
      if (status?.baseline_status === 'stale_refresh_available') {
        setBaselineGuard({
          baseline_version: status.baseline_version,
          current_version:  status.current_version,
          highCvConfirmed:  false,
          cvOptions:        cvOptionsWithSnapshot,
          forceGenerateHint: false,
          busy:             null,
        });
        setSubmittingCv(false);
        return; // Baseline guard handlers resume the flow.
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Workspace CvSection] baseline-status pre-flight failed:',
        e?.message || e,
      );
    }

    // Preflight 2 — pack-canonical pre-emptive bypass. When an
    // application_pack already exists for this job backed by a
    // tailored variant, the user has already committed to generation
    // against this role; pre-set force_generate=true so the legacy
    // `analyse_fit_reject` 409 never surfaces a redundant warning
    // for jobs the user is actively packing. The boolean is
    // threaded into every guard's `captured.forceGenerateHint` so
    // a downstream re-entry (high-CV confirm, baseline refresh,
    // reject confirm) preserves it.
    let packExists = false;
    try {
      const res = await apiFetch(
        `/api/application-packs?job_id=${encodeURIComponent(jobId)}&page_size=1`,
      );
      const items = Array.isArray(res) ? res : (res?.items || []);
      packExists = items.some(
        (p) => p?.job_id === jobId && !!p?.cv_variant_id,
      );
    } catch { /* fail open */ }

    // RC 1.48.6 — High-CV Value Guard. Now the FINAL gate before
    // dispatch (all non-chargeable preflights have completed above).
    // After the user confirms, `handleHighCvConfirm` calls
    // `_dispatchWithCatches` directly with zero further awaits.
    const _sourceCvScore = baselineFit?.baseline?.sub_scores?.cv_sub_score;
    let _highCvAlreadyConfirmed = false;
    try {
      _highCvAlreadyConfirmed =
        sessionStorage.getItem(highCvSessionKey(jobId)) === '1';
    } catch { /* sessionStorage unavailable — fail open to warning */ }
    if (
      typeof _sourceCvScore === 'number'
      && _sourceCvScore >= HIGH_CV_THRESHOLD
      && !_highCvAlreadyConfirmed
    ) {
      setHighCvGuard({
        source_cv_score:   _sourceCvScore,
        cvOptions:         cvOptionsWithSnapshot,
        forceGenerateHint: packExists,
        busy:              false,
      });
      setSubmittingCv(false);
      return; // High-CV guard handler resumes the flow.
    }

    // No modal — straight shot to dispatch.
    setGenerating(true);
    try {
      await _dispatchWithCatches({
        forceGenerate:   packExists,
        highCvConfirmed: false,
        cvOptions:       cvOptionsWithSnapshot,
      });
    } finally {
      setGenerating(false);
      setSubmittingCv(false);
    }
  };

  // ── RC 1.49.7 C5 — Guard handlers (mirror wizard L2619-2709) ─────

  // Baseline guard — Refresh path.
  const handleBaselineRefresh = async () => {
    if (!baselineGuard) return;
    const captured = baselineGuard;
    setBaselineGuard({ ...captured, busy: 'refresh' });
    try {
      await apiFetch(`/api/jobs/${jobId}/refresh-baseline`, { method: 'POST' });
      toast.success('Baseline refreshed — no Credits charged.');
    } catch (e) {
      setBaselineGuard({ ...captured, busy: null });
      toast.error(
        e?.data?.detail?.message || e?.message || 'Baseline refresh failed.',
      );
      return; // leave guard open so the user can retry / cancel
    }
    setBaselineGuard(null);
    setGenerating(true);
    try {
      await _dispatchWithCatches({
        refreshDecision: 'refreshed',
        forceGenerate:   !!captured.forceGenerateHint,
        highCvConfirmed: captured.highCvConfirmed,
        cvOptions:       captured.cvOptions,
      });
    } finally {
      setGenerating(false);
    }
  };

  // Baseline guard — Decline path (backend: no_baseline_protection).
  const handleBaselineDecline = async () => {
    if (!baselineGuard) return;
    const captured = baselineGuard;
    setBaselineGuard(null);
    setGenerating(true);
    try {
      await _dispatchWithCatches({
        refreshDecision: 'declined',
        forceGenerate:   !!captured.forceGenerateHint,
        highCvConfirmed: captured.highCvConfirmed,
        cvOptions:       captured.cvOptions,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleBaselineCancel = () => { setBaselineGuard(null); };

  // Soft reject-gate — Confirm path (retries with force_generate=true).
  const handleRejectConfirm = async () => {
    if (!rejectGuard) return;
    const captured = rejectGuard;
    setRejectGuard(null);
    setGenerating(true);
    try {
      await _dispatchWithCatches({
        forceGenerate:   true,
        highCvConfirmed: captured.highCvConfirmed,
        cvOptions:       captured.cvOptions,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleRejectCancel = () => { setRejectGuard(null); };

  // High-CV guard — Confirm path. RC 1.55 Phase Z.3 (operator-locked,
  // 2026-05-13): direct call to `_dispatchWithCatches` with the
  // preflight-computed `forceGenerateHint`. ZERO `apiFetch` awaits
  // between modal-close and dispatch — eliminates the closure-
  // staleness window that produced ADAPTER_INPUT_ERROR pre-Z.3.
  const handleHighCvConfirm = async () => {
    if (!highCvGuard) return;
    const captured = highCvGuard;
    try {
      sessionStorage.setItem(highCvSessionKey(jobId), '1');
    } catch { /* sessionStorage unavailable — proceed anyway */ }
    setHighCvGuard(null);
    setGenerating(true);
    try {
      await _dispatchWithCatches({
        forceGenerate:   !!captured.forceGenerateHint,
        highCvConfirmed: true,
        cvOptions:       captured.cvOptions,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleHighCvCancel = () => { setHighCvGuard(null); };

  // ── RC 1.51 follow-up A — Continue confirmation warning (Task 1)
  // The user has clicked Continue. If a tailored CV variant was
  // generated this session AND the active selection is the
  // Source/Base CV, surface an alert dialog warning that the
  // downstream flow (Letter / Fit / Pack) will use the Source CV
  // and the tailored CV remains saved in Documents only. Otherwise
  // (no variant ever generated, or active selection IS the variant)
  // proceed straight through. The previous "auto-flip selection
  // back to variant" approach was explicitly cancelled — this
  // warning replaces it.
  const handleContinueClick = () => {
    if (
      generatedResult?.variant_id
      && selectedCv?.type === 'base_cv'
    ) {
      setContinueWarnOpen(true);
      return;
    }
    onContinue?.();
  };

  const handleContinueWarnConfirm = () => {
    setContinueWarnOpen(false);
    onContinue?.();
  };

  // ── Render helpers ───────────────────────────────────────────────
  const isSourceSelected = selectedCv?.type === 'base_cv';
  const isVariantSelected = selectedCv?.type === 'cv_variant';

  // RC 1.52 Phase 3A.4 — read the post-dispatch cooldown for this
  // (user, job) tuple. When active, the Generate/Regenerate CTA is
  // hard-disabled and shows a "Generate again in MM:SS" label so the
  // user can see the gate clearing.
  const cvCooldownKey = cvVariantCooldownKey(primaryCv?.user_id, jobId);
  const cvCooldown = useCooldown(cvCooldownKey);

  const generateLabel = useMemo(
    // RC 1.51 follow-up #2 (operator-locked, 2026-02) — selection-
    // aware label. "Regenerate CV" only when the active selection
    // IS the bound variant (i.e. clicking would replace this
    // variant). For any other selection (Source/Base CV, or a
    // different variant), the label reads "Generate CV" — clicking
    // produces a NEW variant for the active source.
    // RC 1.52 Phase 3A.4 — when the cooldown is active, override
    // the label to surface the countdown so the user can see the
    // gate clearing.
    () => {
      if (cvCooldown.active) return `Generate again in ${cvCooldown.label}`;
      // ── U-9.D — deterministic label flip (2026-02-15) ───────────
      // When a variant is bound (from this session's dispatch OR
      // from the mount-time discovery hook), the button is a
      // regenerate action — even when the selection has not yet
      // flipped to the bound variant. Pre-U-9 the label depended
      // on `selectedCv.id === variant.id`, which often failed to
      // flip when the U-2.CV fallback stamped the URL and the
      // user had a different selection active. Result: a click
      // labelled "Generate CV" actually opened the regenerate
      // confirm dialog → confusing UX. With U-9.D the label
      // always reads "Regenerate CV" when a variant is bound.
      if (generatedResult?.variant_id) return 'Regenerate CV';
      return 'Generate CV';
    },
    [
      generatedResult?.variant_id, selectedCv?.type, selectedCv?.id,
      cvCooldown.active, cvCooldown.label,
    ],
  );

  // RC 1.50 R.2 — Letter-parity preview limits.
  const CV_PREVIEW_LINES = 6;
  const CV_PREVIEW_CHARS = 1200;
  const CV_EXPANDED_CHARS = 4000;
  // RC 1.50 R.2-polish — `stripMarkdownTokens` (lib/markdownStrip.js)
  // turns raw markdown like `# Heading` / `**bold**` / `* bullet` into
  // readable plain prose for the inline preview. NOT a full renderer
  // (no bold weights / heading sizes / italics) — those need a real
  // markdown library, intentionally deferred. Whitespace, line-breaks,
  // and indentation are preserved so the existing <pre> structure and
  // all R.2 source-grep / structural pins stay green.
  const cvDraftPreview = useMemo(() => {
    const txt = generatedResult?.generated_text;
    if (!txt || typeof txt !== 'string') return null;
    const stripped = stripMarkdownTokens(txt);
    const trimmed = stripped.trimStart();
    if (cvPreviewExpanded) {
      return trimmed.length > CV_EXPANDED_CHARS
        ? `${trimmed.slice(0, CV_EXPANDED_CHARS).trimEnd()}…`
        : trimmed;
    }
    const byLine = trimmed.split('\n').slice(0, CV_PREVIEW_LINES).join('\n');
    return byLine.length > CV_PREVIEW_CHARS
      ? `${byLine.slice(0, CV_PREVIEW_CHARS).trimEnd()}…`
      : byLine;
  }, [generatedResult?.generated_text, cvPreviewExpanded]);
  const cvDraftFullLength = (generatedResult?.generated_text || '').length;
  const cvDraftIsTruncated = cvDraftFullLength > 0
    && (cvDraftPreview || '').length < cvDraftFullLength;

  // RC 1.55 Phase Z.6 (operator-locked, 2026-05-13 PM) — Single
  // source of truth for "is the controller currently driving an
  // active CV operation?". Used to suppress the recovery banners
  // while the authoritative `WorkspaceActivityIndicator` is the
  // visible status surface. The three controller-active phases
  // mirror `controller.js::ACTIVE_STATUSES` (dispatching, polling,
  // stabilizing). When this is `true`, the inflight-recovery
  // banners (recovering / exhausted / failed) MUST stay hidden —
  // they exist for the tab-return / hard-refresh recovery case
  // where a stale marker survived but no live operation exists.
  // Strict-rail: frontend-only, no backend / billing / scoring /
  // idempotency / finalize-charge / schema impact. Driven purely
  // by `controllerStatus` from `useOperationLifecycle`.
  const _isControllerActive = (
    controllerStatus === 'dispatching'
    || controllerStatus === 'polling'
    || controllerStatus === 'stabilizing'
  );

  return (
    <div
      data-testid={rootTestId}
      className="flex flex-col gap-5 min-w-0 max-w-full"
    >
      {/* ── Header with live pricing ─────────────────────────────── */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-ndor-ink">
            Tailored CV
          </h2>
          {/* U-9.G — Loaded-from-history chip + U-9.C — Checking
              status — both inline next to the title for one
              consistent surface. */}
          {lookupStatus === 'checking' && (
            <span
              role="status"
              aria-live="polite"
              data-testid="workspace-cv-lookup-checking"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-ndor-ink-muted"
            >
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Checking for existing application materials…
            </span>
          )}
          {lookupStatus === 'ready' && loadedFromHistory && generatedResult?.variant_id && (
            <HistoryBadge testId="workspace-cv-loaded-from-history-badge" />
          )}
          {cvCostLabel != null && (
            <Badge
              className="text-[10px] uppercase tracking-[0.14em] bg-ndor-gold/15 text-ndor-gold border-0"
              data-testid="workspace-cv-price-label"
            >
              {cvCostLabel}
            </Badge>
          )}
        </div>
        <p className="text-xs text-ndor-ink-muted">
          Tailoring is optional — Continue with your Source CV anytime.
        </p>
        {/* RC 1.53 Phase 4.2.1 — visible lifecycle status. The
            activity indicator is hidden when the controller is idle
            / complete. The status line below mirrors the same
            backend-driven phrasing inline so the operator sees the
            phase progression even if they scroll past the chip. */}
        <div className="flex items-center gap-2 min-h-[20px]" data-testid="workspace-cv-lifecycle-status">
          <WorkspaceActivityIndicator
            jobId={jobId}
            actionCode="cv_variant"
            progressLabel={progressLabel}
          />
          {controllerStatus !== 'idle' && controllerStatus !== 'complete' && (
            <span
              className="text-[11px] text-ndor-ink-muted"
              data-testid="workspace-cv-lifecycle-message"
            >
              {getStatusMessage('cv_variant', controllerStatus, progressLabel)}
            </span>
          )}
        </div>
      </div>

      {/* ── Source CV card (RC 1.50 Workspace UX Phase 1) ───────────
            Operator UX direction: Source/Base CV first, then the
            generated tailored artefact, then the preview. The card
            was previously rendered just above the footer, which
            inverted the natural reading order. Moving it here keeps
            it the FIRST concrete card the user sees on entering the
            section. Behaviour, testid, click-to-select handler, and
            the `isSourceSelected` styling rules all preserved
            verbatim. ────────────────────────────────────────────── */}
      <button
        type="button"
        data-testid="workspace-cv-source-card"
        onClick={() => primaryCv?.id && setSelectedCv({
          type: 'base_cv',
          id: primaryCv.id,
          name: primaryCv.original_filename || 'Source CV',
        })}
        disabled={!primaryCv?.id}
        className={
          'rounded-lg border p-3 space-y-1 text-left transition disabled:opacity-50 '
          + (isSourceSelected
            ? 'border-ndor-gold bg-ndor-gold/5'
            : 'border-ndor-line hover:border-ndor-line-strong')
        }
      >
        <div className="flex items-center gap-2 flex-wrap">
          <FileText className="h-4 w-4 text-ndor-ink-muted shrink-0" />
          <p className="text-sm font-medium text-ndor-ink truncate">
            {primaryCv?.original_filename || 'Source CV'}
          </p>
          <Badge className="text-[10px] uppercase tracking-[0.14em] bg-ndor-line/40 text-ndor-ink-muted border-0">
            Source CV
          </Badge>
          {isSourceSelected && (
            <CheckCircle2 className="h-3.5 w-3.5 text-ndor-success ml-auto" aria-hidden />
          )}
        </div>
        <p className="text-[11px] text-ndor-ink-muted">
          {primaryCv?.id
            ? 'Use as-is — no Credits charged.'
            : 'Upload a Source CV in Documents → CVs first.'}
        </p>
      </button>

      {/* ── Generated / recovered variant card (top when present) ── */}
      {/* RC 1.49.7 Layer-A* UX — inline restoring banner. Renders
            ONLY while the URL-driven mount-rehydrate is in flight
            and we haven't bound a result yet. Clears unconditionally
            in the effect's finally block. No modal/toast/dialog
            (workspace UI rule). No charge. ─────────────────────── */}
      {restoring && !generatedResult && (
        <div
          data-testid="workspace-cv-restoring-banner"
          className="rounded-lg border border-ndor-line bg-ndor-bg-subtle p-3 flex items-center gap-2"
        >
          <Loader2 className="h-4 w-4 animate-spin text-ndor-gold" aria-hidden />
          <div className="text-xs text-ndor-ink leading-relaxed">
            <p className="font-medium">Restoring your tailored CV…</p>
            <p className="text-ndor-ink-muted">
              We&apos;re reading your saved variant — no Credits charged.
            </p>
          </div>
        </div>
      )}

      {generatedResult && (
        <button
          type="button"
          data-testid="workspace-cv-generated-card"
          data-cv-context-source-type={searchParams.get('source_cv_type') || ''}
          data-cv-context-source-id={searchParams.get('source_cv_id') || ''}
          data-row-variant-id={generatedResult.variant_id || ''}
          data-row-base-cv-id={primaryCv?.id || ''}
          onClick={() => setSelectedCv({
            type: 'cv_variant',
            id: generatedResult.variant_id,
            name: generatedResult.variant_name,
          })}
          className={
            'rounded-lg border-2 p-3 space-y-2 text-left transition '
            + (isVariantSelected
              ? 'border-ndor-gold bg-ndor-gold/5'
              : 'border-ndor-gold/40 bg-ndor-gold/[0.02] hover:border-ndor-gold')
          }
        >
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="h-4 w-4 text-ndor-gold shrink-0" />
            <p className="text-sm font-medium text-ndor-ink truncate">
              {generatedResult.variant_name || 'Tailored Variant'}
            </p>
            <Badge className="text-[10px] uppercase tracking-[0.14em] bg-ndor-gold/15 text-ndor-gold border-0">
              {/* RC 1.51 follow-up #2 — selection-aware badge.
                    When the active selection is NOT this variant,
                    the card is the "previously generated" surface
                    rather than the active deliverable. */}
              {(selectedCv?.type === 'cv_variant'
                && selectedCv?.id === generatedResult.variant_id)
                ? (generatedResult.recovered_inflight
                  ? 'Recovered · just now'
                  : generatedResult.reused
                    ? 'Reused · saved earlier'
                    : 'Tailored Variant · this attempt')
                : 'Previously generated'}
            </Badge>
            {isVariantSelected && (
              <CheckCircle2 className="h-3.5 w-3.5 text-ndor-success ml-auto" aria-hidden />
            )}
          </div>
          <p className="text-[11px] text-ndor-ink-muted leading-relaxed">
            {generatedResult.recovered_inflight
              ? 'Restored from your last Generate attempt — no new Credits charged.'
              : generatedResult.reused
                ? 'We loaded your existing tailored CV for this job — no new Credits charged.'
                : 'Saved to Documents → CV Variants.'}
          </p>

          {/* ── RC 1.49.7 Slice 2 — Improvement / billing summary
                foundation (operator P0, 2026-02). RENDER-ONLY: every
                line is gated on a backend-supplied field, never
                derived client-side, never falls back to "0" when
                missing. Mirrors the wizard's L401 / L421-436
                disclosures but renders nothing if the wire didn't
                provide the field.  ────────────────────────────── */}
          {(generatedResult.delta != null
            || generatedResult.cv_sub_score_variant != null) && (
            <p
              className="text-[11px] text-ndor-ink-muted"
              data-testid="workspace-cv-generated-score-line"
            >
              {generatedResult.delta != null && (
                <span data-testid="workspace-cv-generated-delta">
                  Δ{' '}
                  <span className="font-mono-ui tabular-nums">
                    {generatedResult.delta > 0 ? '+' : ''}
                    {generatedResult.delta}
                  </span>{' '}
                  pts vs Source CV
                </span>
              )}
              {generatedResult.delta != null
                && generatedResult.cv_sub_score_variant != null
                && <span className="mx-1.5">·</span>}
              {generatedResult.cv_sub_score_variant != null && (
                <span data-testid="workspace-cv-generated-cv-verdict">
                  CV verdict{' '}
                  <span className="font-mono-ui tabular-nums">
                    {generatedResult.cv_sub_score_variant}/100
                  </span>
                </span>
              )}
            </p>
          )}

          {/* Reason-aware charge-line (no client-side derivation —
              shows backend `reason` + `usage_consumed` only when
              both are present on the wire). */}
          {generatedResult.reason && (
            <p
              className={
                'text-[11px] '
                + (generatedResult.charged
                  ? 'text-ndor-warning'
                  : 'text-ndor-ink-muted')
              }
              data-testid="workspace-cv-generated-charge-line"
            >
              {generatedResult.reason === 'cv_quality_improved'
                && 'Material improvement achieved — charged.'}
              {generatedResult.reason === 'no_baseline_protection'
                && 'Charged — no fit baseline existed, so improvement could not be measured.'}
              {generatedResult.reason === 'within_tolerance_band'
                && 'Within NDOR confidence band — comparable to your Source CV. No Credits charged.'}
              {generatedResult.reason === 'cv_quality_no_improvement'
                && 'No material uplift detected — generated CV scored below your Source CV. No Credits charged.'}
              {generatedResult.reason === 'presentation_optimisation_high_cv'
                && 'High-CV presentation polish — partial charge.'}
              {generatedResult.usage_consumed != null && (
                <span className="ml-1">
                  · Charged{' '}
                  <span
                    className="font-mono-ui tabular-nums"
                    data-testid="workspace-cv-generated-usage-consumed"
                  >
                    {generatedResult.usage_consumed}
                  </span>
                  {' '}credit{generatedResult.usage_consumed === 1 ? '' : 's'}
                  {generatedResult.charge_percentage != null
                    && ` (${generatedResult.charge_percentage}%)`}
                </span>
              )}
            </p>
          )}

          {/* ── RC 1.49.7 Slice 3 — CV legacy hydration polish
                (operator P0, 2026-02). When a refreshed/hydrated
                variant carries NEITHER `reason` NOR `delta` NOR
                `usage_consumed`, the wire didn't surface billing
                telemetry (legacy variants persisted before backend
                Option-β shipped). Render a graceful "—" line so
                card heights remain consistent and zero is never
                implied. Per billing_contract.md §6.1.
                Suppressed on:
                  • the live-just-charged path (any of the three
                    fields will be set), AND
                  • the in-flight recovery path (`recovered_inflight`
                    flag is the explicit hint that the row was
                    just charged in this session). ─────────────── */}
          {!generatedResult.recovered_inflight
            && generatedResult.reason == null
            && generatedResult.delta == null
            && generatedResult.usage_consumed == null && (
              <p
                className="text-[11px] text-ndor-ink-muted"
                data-testid="workspace-cv-billing-unavailable"
              >
                Billing details — <span aria-hidden>—</span>{' '}
                <span className="sr-only">
                  not available for this saved variant.
                </span>
              </p>
            )}
        </button>
      )}

      {/* ── U-14.C (2026-02-15) — Improvement / Defensibility cards ───
          OPERATOR-LOCKED. Restores the "Why am I stronger now?" +
          "How do I defend this at interview?" panels that were
          rendered by the legacy wizard modal (`JobApplicationWizard
          .jsx` L489) but lost during the RC 1.49.7 Phase 1
          Workspace-Route migration. Backend continues to generate
          `optimisation_artifacts` (see `routes/cv_variants.py`
          L2842-2863) — only the rendering was missing.

          Wrapped in a workspace-specific testid container so the
          U-14 source-pin tests can assert presence/absence by DOM
          path. The component itself returns `null` when
          `artifacts` is empty / missing — so non-charged variants
          and pre-P1 rows render cleanly with no visual residue.
          ──────────────────────────────────────────────────────── */}
      {generatedResult
       && generatedResult.optimisation_artifacts
       && (
         generatedResult.optimisation_artifacts.improvement_map
         || generatedResult.optimisation_artifacts.defensibility_map
       ) && (
        <div
          data-testid="workspace-cv-optimisation-explainer"
          data-has-improvement-map={generatedResult.optimisation_artifacts?.improvement_map ? 'true' : 'false'}
          data-has-defensibility-map={generatedResult.optimisation_artifacts?.defensibility_map ? 'true' : 'false'}
          className="px-4 pb-3"
        >
          <OptimisationDefenseCards
            artifacts={generatedResult.optimisation_artifacts}
          />
        </div>
      )}

      {/* ── RC 1.50 R.2 — Inline draft preview. Visual parity with
            CoverLetterSection (line 723-731): same monospace +
            muted styling + border-top separator. Rendered as a
            SIBLING after the success-card <button> (so the Slice 2
            source-grep pin `workspace-cv-generated-card[\s\S]*?
            </button>` stays green and we avoid the nested-button
            HTML invalidity that an in-button preview would cause).
            Inline only — no modal / dialog / drawer / popover.
            Expand/collapse is a pure local state-flip. NEVER
            triggers /generate. NEVER charges. ────────────────── */}
      {generatedResult && cvDraftPreview && (
        <div
          data-testid="workspace-cv-draft-preview-card"
          className="-mt-1 px-4 pb-3"
        >
          <pre
            data-testid="workspace-cv-draft-preview"
            className="font-mono-ui text-[11px] text-ndor-ink-muted whitespace-pre-wrap break-words leading-relaxed border-t border-ndor-line pt-2 m-0"
          >
            {cvDraftPreview}
            {cvDraftIsTruncated && !cvPreviewExpanded && '\n…'}
          </pre>
          {cvDraftIsTruncated && (
            <button
              type="button"
              data-testid="workspace-cv-draft-preview-toggle"
              onClick={() => setCvPreviewExpanded((v) => !v)}
              className="mt-1.5 text-[11px] font-medium text-ndor-gold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ndor-gold/50 rounded"
            >
              {cvPreviewExpanded ? 'Collapse' : 'Expand'}
            </button>
          )}
        </div>
      )}

      {/* ── RC 1.49.7 Slice 2 — In-flight recovery status banner
            (operator P0, 2026-02). Inline ONLY (no modal/sheet/
            drawer/popover). Reflects the recovery effect's polling
            state. NEVER triggers /generate. Cleared automatically
            on bind or on poll-exhaustion. ───────────────────────
            RC 1.55 Phase Z.6 (operator-locked, 2026-05-13 PM) —
            Gate ALL three recovery banners on `!_isControllerActive`.
            The top WorkspaceActivityIndicator is the AUTHORITATIVE
            active-run indicator (driven by the
            `useOperationLifecycle` controller status: dispatching /
            polling / stabilizing). The recovery banners are
            recovery-only — they exist to surface a prior-session
            marker that lacks a live controller op. When the
            controller IS active they were producing conflicting
            UX (e.g. top spinner shows "Preparing your tailored
            CV…" while the lower banner says "Check Documents → CV
            Variants — no new Credits were charged"). Suppress the
            banners during any active controller phase so a single
            authoritative indicator is visible at a time. Recovery
            banners CAN still appear once the controller returns to
            `idle` and a stale marker is still present (the genuine
            tab-return / hard-refresh recovery case). ─────────── */}
      {!generatedResult && !_isControllerActive && recoveryStatus === 'recovering' && (
        <div
          data-testid="workspace-cv-recovering-banner"
          role="status"
          aria-live="polite"
          className="rounded-lg border border-ndor-gold/40 bg-ndor-gold/[0.04] p-3 flex items-start gap-2"
        >
          <Loader2 className="h-4 w-4 text-ndor-gold shrink-0 animate-spin mt-0.5" aria-hidden />
          <div className="text-xs text-ndor-ink leading-relaxed space-y-0.5">
            {/* F4 (operator-locked, 2026-02 patch B1.F4): copy
                conveys the canonical B1 pending state. The id-
                driven recovery loop is polling `GET /api/cv-
                variants/{id}` while the row is queued|running;
                "Generating your tailored CV…" is the honest
                in-progress label. Charge has NOT fired
                (finalize-charge gate refuses 409 on non-complete
                rows). */}
            <p className="font-medium">Generating your tailored CV…</p>
            <p className="text-[11px] text-ndor-ink-muted">
              We&apos;re finishing your last Generate attempt —
              no new Credits will be charged.
            </p>
          </div>
        </div>
      )}
      {!generatedResult && !_isControllerActive && recoveryStatus === 'exhausted' && (
        <div
          data-testid="workspace-cv-recovery-exhausted-banner"
          role="status"
          aria-live="polite"
          className="rounded-lg border border-ndor-edge bg-ndor-surface p-3 flex items-start gap-2"
        >
          <AlertTriangle className="h-4 w-4 text-ndor-ink-muted shrink-0 mt-0.5" aria-hidden />
          <div className="text-xs text-ndor-ink leading-relaxed space-y-0.5">
            <p className="font-medium">
              We couldn&apos;t automatically restore your last generated CV.
            </p>
            <p className="text-[11px] text-ndor-ink-muted">
              Check{' '}
              <a
                href="/documents"
                className="underline decoration-dotted underline-offset-2 hover:text-ndor-ink"
              >
                Documents → CV Variants
              </a>
              {' '}— no new Credits were charged.
            </p>
          </div>
        </div>
      )}
      {/* ── RC 1.51 follow-up B1.F3 — failed-generation advisory
            (operator-locked, 2026-02). Surfaced when the B1
            id-driven recovery loop reads `run_status='failed'` from
            the canonical row. The marker is cleared by the
            handler, so this block is purely informational — the
            user clicks Generate to retry; no Credits were
            consumed (finalize-charge gate at the backend refuses
            to charge a failed row, returning 422). ─────────── */}
      {!generatedResult && !_isControllerActive && recoveryStatus === 'failed' && (
        <div
          data-testid="workspace-cv-recovery-failed-banner"
          role="status"
          aria-live="polite"
          className="rounded-lg border border-amber-400/40 bg-amber-500/10/60 p-3 flex items-start gap-2"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" aria-hidden />
          <div className="text-xs text-ndor-ink leading-relaxed space-y-0.5">
            <p className="font-medium">
              Your last CV generation didn&apos;t complete.
            </p>
            <p className="text-[11px] text-ndor-ink-muted">
              Click Generate to try again — no Credits were charged.
            </p>
          </div>
        </div>
      )}

      {/* ── RC 1.49.7 C5 — Baseline guard (inline, NOT a modal) ─── */}
      {baselineGuard && (
        <div
          data-testid="workspace-cv-baseline-guard"
          role="alert"
          className="rounded-lg border-2 border-amber-400/40 bg-amber-500/10/60 p-3 space-y-3"
        >
          <div className="flex items-start gap-2">
            <Sparkles className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" aria-hidden />
            <div className="text-xs text-ndor-ink leading-relaxed space-y-1.5">
              <p
                className="font-medium text-sm"
                data-testid="workspace-cv-baseline-guard-title"
              >
                Updated scoring methodology
              </p>
              <p className="text-ndor-ink-muted">
                NDOR&apos;s scoring methodology has improved since your previous
                analysis. We can refresh your prior baseline at no charge to
                preserve the most accurate comparison.
              </p>
              {(baselineGuard.baseline_version || baselineGuard.current_version) && (
                <div
                  data-testid="workspace-cv-baseline-guard-version-chip"
                  className="flex flex-wrap items-center gap-2 rounded-md bg-ndor-surface/60 px-2 py-1 text-[11px]"
                >
                  <span className="font-medium text-ndor-ink-muted">Versions:</span>
                  <span>
                    Baseline:&nbsp;
                    <span className="font-mono">
                      {baselineGuard.baseline_version || 'unstamped'}
                    </span>
                  </span>
                  <span aria-hidden>·</span>
                  <span>
                    Current:&nbsp;
                    <span className="font-mono">
                      {baselineGuard.current_version || '—'}
                    </span>
                  </span>
                </div>
              )}
              <ul className="space-y-1 text-[11px] text-ndor-ink-muted">
                <li className="flex items-start gap-1.5">
                  <span className="mt-0.5 text-emerald-600">✓</span>
                  <span>
                    Refreshing is <strong>free</strong> — no Credits charged.
                  </span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="mt-0.5 text-emerald-600">✓</span>
                  <span>
                    Refresh preserves your charge protection (the ±5 confidence
                    band still waives charges when the tailored CV doesn&apos;t
                    materially improve on the refreshed baseline).
                  </span>
                </li>
                <li className="flex items-start gap-1.5">
                  <AlertTriangle className="mt-0.5 h-3 w-3 text-amber-600 shrink-0" aria-hidden />
                  <span>
                    Skipping leaves no baseline to compare against —
                    tailoring will charge in full regardless of quality
                    improvement.
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBaselineCancel}
              disabled={!!baselineGuard.busy}
              data-testid="workspace-cv-baseline-guard-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBaselineDecline}
              disabled={!!baselineGuard.busy}
              data-testid="workspace-cv-baseline-guard-decline-btn"
            >
              {baselineGuard.busy === 'decline' && (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              )}
              Continue Without Refresh
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleBaselineRefresh}
              disabled={!!baselineGuard.busy}
              data-testid="workspace-cv-baseline-guard-refresh-btn"
            >
              {baselineGuard.busy === 'refresh' && (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              )}
              Refresh My Baseline (Free)
            </Button>
          </div>
        </div>
      )}

      {/* ── RC 1.49.7 C5 — Soft reject-gate (inline, NOT a modal) ── */}
      {rejectGuard && (
        <div
          data-testid="workspace-cv-reject-guard"
          role="alert"
          className="rounded-lg border-2 border-ndor-warning/40 bg-ndor-warning/[0.05] p-3 space-y-3"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-ndor-warning shrink-0 mt-0.5" aria-hidden />
            <div className="text-xs text-ndor-ink leading-relaxed space-y-1">
              <p
                className="font-medium text-sm"
                data-testid="workspace-cv-reject-guard-title"
              >
                Low fit detected — generate anyway?
              </p>
              <p
                className="text-ndor-ink-muted"
                data-testid="workspace-cv-reject-guard-description"
              >
                Your baseline fit assessment for this role landed
                {rejectGuard.fit_score != null ? (
                  <>
                    {' '}at{' '}
                    <span
                      className="font-mono tabular-nums"
                      data-testid="workspace-cv-reject-guard-score"
                    >
                      {rejectGuard.fit_score}/100
                    </span>
                  </>
                ) : (
                  <> in the low-fit band</>
                )}
                {' '}with a verdict of{' '}
                <span className="font-medium">
                  {rejectGuard.final_decision || 'reject'}
                </span>
                . NDORFlow governance treats this as a directional signal
                — not a hard block. You can still generate a tailored CV
                if you have additional context to add or simply want to
                proceed.
                {' '}
                <span
                  className="font-medium text-ndor-warning"
                  data-testid="workspace-cv-reject-guard-charge-notice"
                >
                  Proceeding here is an explicit override:
                  generation will incur the FULL Credit cost even if
                  the resulting score change is within the usual
                  tolerance band.
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRejectCancel}
              disabled={generating}
              data-testid="workspace-cv-reject-guard-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleRejectConfirm}
              disabled={generating || workspaceBusy}
              data-testid="workspace-cv-reject-guard-confirm-btn"
            >
              {generating
                ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              Generate Anyway
            </Button>
          </div>
        </div>
      )}

      {/* ── U-5.CV.A — Regenerate confirm gate (2026-02-15) ─────── */}
      {regenerateConfirm && (
        <div
          data-testid="cv-regenerate-confirm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="cv-regenerate-confirm-title"
          className="my-4 rounded-lg border border-amber-500/60 bg-amber-500/10 p-4 text-sm"
        >
          <div
            id="cv-regenerate-confirm-title"
            className="font-medium text-amber-100 mb-1.5"
          >
            Regenerate tailored CV?
          </div>
          <div className="text-amber-50/80 mb-3">
            You already have a tailored CV for this job. Generating a new one
            will spend Credits and replace the version shown above.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRegenerateConfirm(null)}
              data-testid="cv-regenerate-cancel"
            >
              Keep existing
            </Button>
            <Button
              variant="default"
              size="sm"
              data-testid="cv-regenerate-confirm-go"
              disabled={
                generating
                || creditsLoading
                || !primaryCv?.id
                || workspaceBusy
              }
              onClick={() => {
                // ── U-5.CV.A — operator-locked confirm path. ─────
                // RESET the session-guard FIRST so the dispatch
                // below mints a FRESH idempotency key. Without this
                // reset, the active session (still inside TTL +
                // stabilisation window from the previous successful
                // generation) would reuse the old key → backend
                // dedupes → `reused=true` → user sees the same CV
                // they were trying to regenerate.
                try {
                  resetSession(primaryCv?.user_id, jobId, 'cv_variant');
                } catch { /* noop — best-effort */ }
                setRegenerateConfirm(null);
                // Open the options sheet — the existing chargeable
                // flow takes over from here (handleOptionsConfirm
                // → _dispatchWithCatches → _runGenerateInner).
                setOptionsOpen(true);
              }}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Regenerate
            </Button>
          </div>
        </div>
      )}

      {/* ── RC 1.49.7 C5 — High-CV Value Guard (inline, NOT a modal) ── */}
      {highCvGuard && (
        <div
          data-testid="workspace-cv-high-cv-guard"
          role="alert"
          className="rounded-lg border-2 border-ndor-gold/40 bg-ndor-gold/[0.04] p-3 space-y-3"
        >
          <div className="flex items-start gap-2">
            <Sparkles className="h-4 w-4 text-ndor-gold shrink-0 mt-0.5" aria-hidden />
            <div className="text-xs text-ndor-ink leading-relaxed space-y-1">
              <p
                className="font-medium text-sm"
                data-testid="workspace-cv-high-cv-guard-title"
              >
                Your Source CV is already strong
              </p>
              <p
                className="text-ndor-ink-muted"
                data-testid="workspace-cv-high-cv-guard-description"
              >
                Your current Source CV scores{' '}
                <span
                  className="font-mono tabular-nums"
                  data-testid="workspace-cv-high-cv-guard-score"
                >
                  {highCvGuard.source_cv_score ?? ''}/100
                </span>
                {' '}on this role. Further optimisation may mainly
                improve presentation and clarity rather than your
                underlying fit.{' '}
                <span
                  className="font-medium"
                  data-testid="workspace-cv-high-cv-guard-charge-notice"
                >
                  Proceeding is an explicit override:
                  this uses Credits at 50% of the standard tailoring
                  cost for a presentation-only refinement, or the
                  full cost when the new variant beats your Source
                  CV by 6 points or more.
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleHighCvCancel}
              disabled={generating}
              data-testid="workspace-cv-high-cv-guard-cancel-btn"
            >
              Use current CV
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleHighCvConfirm}
              disabled={generating || workspaceBusy}
              data-testid="workspace-cv-high-cv-guard-confirm-btn"
            >
              {generating
                ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              Generate anyway
            </Button>
          </div>
        </div>
      )}

      {/* ── RC 1.49.7 Slice 4 — Three-control footer (Exit · Generate ·
            Continue). Operator P0 2026-02. The legacy per-section
            "Back" button is REMOVED — the workspace is now strictly
            forward-only. CV is the entry section, so we surface an
            "Exit workspace" affordance routing back to the job
            detail page (the only meaningful "back" target from
            here). The header-level `workspace-back-to-job` link
            provides the same exit globally; this footer button is
            an inline equivalent for users whose attention is
            already on the footer controls. Letter / Fit / Pack
            footers no longer render any exit affordance — users
            backtrack via the section nav (already-visited tabs
            stay clickable per `furthestIndex`) or the header
            link. ─────────────────────────────────────────────── */}
      <footer
        data-testid="workspace-section-footer"
        /* RC 1.55 P0 Mobile pass — below the `xs` breakpoint (360px,
           Galaxy Fold closed and similar) the row STACKS so every
           button gets a full-width tap target with no truncation.
           At `xs+` the row wraps inline; at `sm+` it sits inline.
           Reserved bottom safe-area so iOS Safari URL bar / Android
           nav don't eat the row. */
        className="sticky bottom-0 z-10 -mx-3 sm:-mx-4 px-3 sm:px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] flex flex-col xs:flex-row xs:flex-wrap xs:items-center xs:justify-between gap-2 border-t border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={onExit}
          /* RC 1.53 Phase 4.2.1.1.d — navigation always free. */
          data-testid="workspace-cv-exit-button"
          className="w-full xs:w-auto px-2 sm:px-3"
        >
          <ArrowLeft className="mr-1 sm:mr-1.5 h-3.5 w-3.5" />
          {/* RC 1.55 P0 Mobile pass — short label on narrow widths,
              full label on tablet+. Tap target stays ≥36px via
              size="sm". */}
          <span className="hidden sm:inline">Exit workspace</span>
          <span className="sm:hidden">Exit</span>
        </Button>

        <Button
          variant="default"
          size="sm"
          onClick={handleGenerate}
          disabled={
            generating
            || creditsLoading
            || !primaryCv?.id
            || !!baselineGuard
            || !!rejectGuard
            || !!highCvGuard
            || cvCooldown.active
            /* RC 1.53 Phase 4.2.1.1 — also lock the CTA whenever the
               OperationLifecycleController is in any active state
               (dispatching | polling | stabilizing). This keeps the
               button disabled through finalize-charge / hydration /
               credits refresh — never a stale-balance re-click window. */
            || controllerStatus === 'dispatching'
            || controllerStatus === 'polling'
            || controllerStatus === 'stabilizing'
            /* RC 1.53 Phase 4.2.1.1.d — workspace-wide Generate-lock.
               If ANY section is running an operation (CV, Letter,
               Interview Prep, Pack), lock this CTA too. Navigation
               (Continue / Exit) stays free. */
            || workspaceBusy
            /* RC 1.53 Phase 4.3 — Double-click race fix
               (operator, 2026-05-12 PM). The Generate button opens
               the GenerateCvOptionsSheet, which then calls
               handleOptionsConfirm → async pre-flights →
               setGenerating(true). Without this gate the main
               Generate button is still clickable while the sheet
               is open AND for the ~1s pre-flight window after the
               sheet closes — a confirmed-in-the-wild double-click
               path that double-charged 9+9 = 18 credits in soak
               run #3. Locking on `optionsOpen` AND `submittingCv`
               closes both halves of the race. The flag is cleared
               whenever a guard intercepts (so the guarded re-entry
               path remains clickable). */
            || optionsOpen
            || submittingCv
            /* U-9.B — lock Generate while mount-time existing-artefact
               discovery is in flight. Closes the pre-U-9 race window
               where a click between mount and lookup resolution
               dispatched a generation the user did not consciously
               want. */
            || lookupStatus !== 'ready'
          }
          data-testid="workspace-cv-generate-button"
          data-controller-status={controllerStatus}
          data-cooldown-active={cvCooldown.active ? '1' : '0'}
          data-cooldown-remaining-ms={cvCooldown.active ? String(cvCooldown.remainingMs) : ''}
          title={cvCooldown.active
            ? `Generate is on cooldown for ${cvCooldown.label}. This prevents accidental re-clicks while your wallet syncs.`
            : undefined}
          /* RC 1.55 P0 Mobile pass — grow to fill remaining row width
             on narrow viewports so the primary action is always the
             biggest tap target. */
          className="w-full xs:w-auto xs:flex-1 xs:min-w-0 sm:flex-none px-2 sm:px-3"
        >
          {generating
            ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin shrink-0" />
            : <Sparkles className="mr-1.5 h-3.5 w-3.5 shrink-0" />}
          {/* RC 1.55 P0 Mobile pass — on very narrow viewports the
              full `generateLabel` ("Generate CV" / "Regenerate CV" /
              "Generate again in 12s") truncates to "G…". Show a
              compact "Generate" / "Wait …" instead on widths under
              the `xs` breakpoint, then full label from `xs` upward.
              The full label still ships in `aria-label` for screen
              readers. */}
          <span className="hidden xs:inline truncate" aria-hidden="true">
            {generateLabel}
          </span>
          <span className="xs:hidden truncate" aria-hidden="true">
            {cvCooldown.active
              ? 'Wait'
              : generateLabel.startsWith('Re') ? 'Regen' : 'Generate'}
          </span>
          <span className="sr-only">{generateLabel}</span>
        </Button>

        <Button
          variant="default"
          size="sm"
          onClick={handleContinueClick}
          /* RC 1.53 Phase 4.2.1.1.d — navigation always free. */
          data-testid="workspace-section-continue-button"
          className="w-full xs:w-auto px-2 sm:px-3"
        >
          Continue
          <ArrowRight className="ml-1 sm:ml-1.5 h-3.5 w-3.5" />
        </Button>
      </footer>

      {creditsLoading && !generating && (
        <p
          className="text-[11px] text-ndor-ink-muted"
          data-testid="workspace-cv-credits-loading-hint"
        >
          Loading your wallet…
        </p>
      )}

      {/* ── RC 1.51 follow-up A — CV Generation Options Sheet
            (Task 2). Reuses the wizard's GenerateCvOptionsSheet
            verbatim so the Style preset → (template_type, tone_type,
            length_preference) mapping + Additional Context cap stay
            in lockstep across both surfaces. The sheet's onConfirm
            forwards the resolved field set to handleOptionsConfirm,
            which carries it through the high-CV / baseline / reject
            guard chain so the user's options are never lost when a
            guard intervenes. ────────────────────────────────────── */}
      <GenerateCvOptionsSheet
        open={optionsOpen}
        onOpenChange={setOptionsOpen}
        onConfirm={handleOptionsConfirm}
        generating={generating}
      />

      {/* ── RC 1.51 follow-up A — Continue confirmation warning
            (Task 1). Surfaces only when the user clicks Continue
            with a tailored CV in hand AND the active selection is
            still the Source/Base CV. Confirm proceeds with the
            Source CV; Cancel keeps the user on this section so
            they can re-select the Tailored Variant card before
            moving on. The tailored CV remains saved in Documents
            either way (PROD path: Documents → CV Variants). ──── */}
      <AlertDialog
        open={continueWarnOpen}
        onOpenChange={setContinueWarnOpen}
      >
        <AlertDialogContent
          data-testid="workspace-cv-continue-warning-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              Continue with your Source CV?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have a tailored CV ready, but your active selection
              is your Source CV. If you continue, the rest of the
              flow (Cover Letter, Analyse Fit, Application Pack)
              will use the <strong>Source CV</strong>. Your tailored
              CV stays saved in <strong>Documents → CV Variants</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="workspace-cv-continue-warning-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleContinueWarnConfirm}
              data-testid="workspace-cv-continue-warning-confirm-btn"
            >
              Use Source CV anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
